import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { erApiFeil, type ApiFeil } from '../api/feil.ts';
import { useHypoteseNotater, useHypoteseOvergang, useLeggTilHypoteseNotat, type AdminUserRef, type Overgang, type OverrideKind, type RejectReason, type ReviewCheck, type StopReason } from '../api/useHypoteseBehandling.ts';
import type { Hypothesis, HypothesisConfig, HypothesisSummary } from '../api/useHypotheses.ts';
import { useHypoteseListe } from '../api/useHypoteseBehandling.ts';
import { useKvitterVarsel, useSammenslaaingKandidater, useVarsleMeg } from '../api/useHypoteseHukommelse.ts';
import { useHypoteseProgrammer, type HypothesisProgramRef } from '../api/useHypoteseProgrammer.ts';
import { BetaprogramSeksjon, programLoggRader, ProgramDoer } from './Betaprogram.tsx';
import { useHypoteseMoeter, type HypothesisMeetingEntry } from '../api/useMoeter.ts';
import { moeteNavn, tallTekst } from './moeteLesning.ts';
import { DataDialog, Interesse, kanGiData } from './Interesse.tsx';
import { kanFoelges } from './kilder.ts';
import { ByggerPaa, LignerPaaKandidater, SlaaSammenDialog } from './Sammenslaaing.tsx';
import { Sporingslinje, type SporLedd } from '../innboks/Sporingslinje.tsx';
import { saksnr } from '../innboks/saksnummer.ts';
import { useMe } from '../api/useMe.ts';
import { useTilgang } from '../skall/tilgang.ts';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { HypotesePille } from '../status/Pille.tsx';
import { hypoteseOppforing, tokenVar } from '../status/status.ts';
import { Fot, Radiogruppe, Tekstknapp, Varsel } from './deler.tsx';
import { Funnliste, Konklusjonsblokk, Sjekkliste } from './EtterValgt.tsx';
import { GrunnlagSeksjon, NotisSeksjon, notisSkrivesAv } from './Grunnlag.tsx';
import { kanEndreLenker, LenkerSeksjon } from './Lenker.tsx';
import { nummerTekst } from './Kvittering.tsx';
import {
  andreSetning,
  dagerTil,
  datoMedAar,
  eierDeaktivert,
  endredeLedd,
  endringTekst,
  foerPausen,
  fornavn,
  handlingerFor,
  hvem,
  isoDag,
  konklusjonVenter,
  kvalitetssjekkTekst,
  leggTilDager,
  leggTilMaaneder,
  loefteFor,
  loefteVed,
  loesbareLaaser,
  MAKS_TRENGER_MER,
  navnAv,
  revisjonenFoer,
  rollerPaa,
  statusNavn,
  stor,
  testetPeriode,
  tidspunktFor,
  ventendeJustering,
  type Handling,
  type HandlingK,
} from './lesning.ts';
import { HYPOTESER } from './rommet.ts';
import { kortDato, kortDatoAv, kortDatoMedAarAv, tall } from './setning.ts';
import type { Ventende } from './useAngre.ts';

/**
 * Detaljen for en hypotese i innboksen og «Til deg» (H2.3, `admin-hypoteser.jsx`: DetaljMottatt,
 * DetaljVurderes, DetaljBeslutning, DetaljParkert). Rekkefølgen er konseptets: sporet (hvor den
 * er fra og hvem som har den) → tittelen → statusblokken med sporingslinjen, «Nå» og «Neste
 * steg» → setningen → faktaene → vurderingen, spørsmålet, svaret, notatet og loggen → foten.
 *
 * Reglene som bor her:
 * - Neste steg er den innloggedes, som fylte knapper i statusens flate (DD-56); de andres står
 *   som ÉN setning. Arkene åpner feltene i blokken, aldri en modal. Esc lukker arket.
 * - Foten har ÉTT primærvalg (DD-58). Uten et valg står «Lagre» sperret med grunnen.
 * - Avvist krever kode og ENDRET mal — nektet her, før tjeneren (DD-09).
 * - Svaret står åpent; notatet bak `n` for leser og opp (DD-66, Kh7).
 * - En overgang som sender svar, holdes tilbake i angre-vinduet av siden (`useAngre`).
 */

/** Plassen malen lar stå (DD-09): den stiplede, gule biten som må skrives om. Leietakerens maler bruker den. */
export const MAL_PLASSER = ['skriv hvorfor', 'skriv spørsmålet'];
export const harMalPlass = (tekst: string): boolean => MAL_PLASSER.some((p) => tekst.includes(p));

const SVAR_MAKS = 500;
const TM_MIN = 20;

/** Det som er skrevet og valgt i arkene, per hypotese. Overlever radbytte, aldri en ny innlasting (K8). */
interface Ark {
  valgt: HandlingK | null;
  notatAapent: boolean;
  notat: string;
  kortAapent: boolean;
  kortDato: string;
  tm: string;
  parkDato: string;
  parkSvar: string;
  velgEier: string;
  velgSvar: string;
  avvisKode: RejectReason | undefined;
  avvisSvar: string;
  unparkSvar: string;
  kanBomme: ReviewCheck['canFail'] | undefined;
  kilde: ReviewCheck['sourceCheck'] | undefined;
  baseline: ReviewCheck['baselineCheck'] | undefined;
  merknad: string;
  /** Svarmalen feltet ble startet fra (`config.responseTemplates`), sendt som `templateId`. */
  maler: Partial<Record<MalFelt, string>>;
  // Etter valgt (H2.3 andre runde).
  godkjennSvar: string;
  avslaaSvar: string;
  losneKind: OverrideKind | undefined;
  losneGrunn: string;
  gvEier: string;
  gvBesl: string;
  pauseDato: string;
  pauseSvar: string;
  stoppKode: StopReason | undefined;
  stoppLaerte: string;
  stoppBlokk: string;
  stoppData: string;
  stoppSvar: string;
  bekreftSvar: string;
  tilbakeMerknad: string;
  /** H3.1: notisen til sluttbrukerne, skrevet av eieren. Lagres ikke før den sendes. */
  notis: string;
  /** H3.3: «Testet før — se konklusjonen» peker på hypotesen den ble testet i (`relatedHypothesisId`). */
  avvisRelatert: string;
}
type SvarMal = HypothesisConfig['responseTemplates'][number];
type MalFelt = 'tm' | 'park' | 'velg' | 'avvis' | 'godkjenn' | 'avslaa' | 'pause' | 'stopp' | 'bekreft';
type SvarArk = 'tm' | 'parkSvar' | 'velgSvar' | 'godkjennSvar' | 'avslaaSvar' | 'pauseSvar' | 'bekreftSvar';
/** Arket, feltet det fyller og malen for avgjørelsen (`HypothesisTemplateUse`). Avvis og stopp starter fra kodens mal. */
const MAL_FOR: Partial<Record<HandlingK, { felt: SvarArk; bruk: SvarMal['use'] }>> = {
  tm: { felt: 'tm', bruk: 'trenger_mer' },
  park: { felt: 'parkSvar', bruk: 'parkert' },
  velg: { felt: 'velgSvar', bruk: 'valgt' },
  godkjenn: { felt: 'godkjennSvar', bruk: 'justering' },
  avslaa: { felt: 'avslaaSvar', bruk: 'justering' },
  pause: { felt: 'pauseSvar', bruk: 'pa_pause' },
  bekreft: { felt: 'bekreftSvar', bruk: 'konkludert' },
};

const TOMT_ARK: Ark = {
  valgt: null,
  notatAapent: false,
  notat: '',
  kortAapent: false,
  kortDato: '',
  tm: '',
  parkDato: '',
  parkSvar: '',
  velgEier: '',
  velgSvar: '',
  avvisKode: undefined,
  avvisSvar: '',
  unparkSvar: '',
  kanBomme: undefined,
  kilde: undefined,
  baseline: undefined,
  merknad: '',
  maler: {},
  godkjennSvar: '',
  avslaaSvar: '',
  losneKind: undefined,
  losneGrunn: '',
  gvEier: '',
  gvBesl: '',
  pauseDato: '',
  pauseSvar: '',
  stoppKode: undefined,
  stoppLaerte: '',
  stoppBlokk: '',
  stoppData: '',
  stoppSvar: '',
  bekreftSvar: '',
  tilbakeMerknad: '',
  notis: '',
  avvisRelatert: '',
};

/** Malen for en avgjørelse — den første med den bruken. Utgangspunkt, aldri hele svaret (DD-09). */
const malFor = (config: HypothesisConfig | undefined, bruk: SvarMal['use']): SvarMal | undefined => config?.responseTemplates.find((m) => m.use === bruk);

/** Svaret med malen det ble startet fra, som kontraktens `HypothesisResponseCreate`. */
const svarMed = (tekst: string, malId: string | undefined) => ({ text: tekst.trim(), ...(malId ? { templateId: malId } : {}) });
const arkene = new Map<string, Ark>();
/** Kalles når en overgang er sendt: det som var skrevet, er nå svaret. */
export const glemArk = (id: string) => arkene.delete(id);

export interface DetaljProps {
  h: Hypothesis;
  config: HypothesisConfig | undefined;
  megId: string | undefined;
  megNavn: string | undefined;
  rom: { behandler: boolean; leserPluss: boolean };
  /** «Innboks», «Til deg» eller «Registeret» — sporet over tittelen og tilbakepilen under 900 px. */
  fra: { navn: string; til: string };
  /**
   * Detaljen står alene, uten lista ved siden av (registeret, konseptets skjerm 6): veien tilbake
   * er en lenke med sidens navn over sporet, og sporet sier ikke navnet en gang til (DD-67).
   */
  alene?: boolean;
  /** «2 av 12» i stripen under 900 px. */
  posisjon?: string;
  /** Adressen til juster-siden (konseptets skjerm 6b): «Juster før start …» er en egen side, ikke et ark. */
  justerTil: string;
  /** Det som venter i angre-vinduet, hvis det gjelder denne. */
  venter: Ventende | null;
  onHold: (v: Ventende) => void;
  /** Feilen tjeneren ga på det som ble sendt fra denne hypotesen. */
  feil: ApiFeil | null;
  onNeste?: () => void;
  onKvittering: (tekst: string) => void;
  onFeil: (e: unknown) => void;
}

export function HDetalj(p: DetaljProps) {
  const { h, config, megId, rom } = p;
  const smal = useSmal();
  const roller = rollerPaa(h, megId, config, rom);
  const handlinger = handlingerFor(h, roller);
  const andre = andreSetning(h, roller, config, handlinger);
  const innsender = navnAv(h.submittedBy);
  const innsenderFor = fornavn(h.submittedBy);
  const iDag = isoDag(new Date());
  const nr = nummerTekst(h.number);
  // H3.2 (W11b): møtene hypotesen har vært på, med tallene — bare leser og opp leser dem.
  const moeter = useHypoteseMoeter(h.status === 'utkast' ? null : h.id, rom.leserPluss);

  // H3.3 (W11): en kilde fra kildelisten som er bekreftet, gir «Kilden: Ja, finnes» forhåndsvalgt — hun kan endre det.
  const [ark, setArk] = useState<Ark>(() => arkene.get(h.id) ?? (h.status === 'mottatt' && h.metricSource && h.sourceStatus === 'verifisert' ? { ...TOMT_ARK, kilde: 'ja' } : TOMT_ARK));
  const sett = useCallback(<K extends keyof Ark>(k: K, v: Ark[K]) => setArk((a) => ({ ...a, [k]: v })), []);
  const arkRef = useRef(ark);
  arkRef.current = ark;
  useEffect(
    () => () => {
      const a = arkRef.current;
      if (JSON.stringify(a) === JSON.stringify(TOMT_ARK)) arkene.delete(h.id);
      else arkene.set(h.id, a);
    },
    [h.id],
  );
  // Statusen har endret seg (en overgang er sendt, eller noen andre kom først): arkene hørte
  // til den forrige. Teksten i et ark som ble nektet av tjeneren står — det er feilen som sier fra.
  const forrigeStatus = useRef(h.status);
  useEffect(() => {
    if (forrigeStatus.current !== h.status) {
      forrigeStatus.current = h.status;
      if (!p.feil) setArk((a) => ({ ...TOMT_ARK, notat: a.notat, notatAapent: a.notatAapent }));
    }
  }, [h.status, p.feil]);

  const notater = useHypoteseNotater(h.id, rom.leserPluss);
  const leggTilNotat = useLeggTilHypoteseNotat();
  const direkte = useHypoteseOvergang();

  // --- H3.1: grunnlaget og notisen ------------------------------------------------------------
  const tilgang = useTilgang();
  const tenantName = useMe().data?.tenantName;
  const [velgerAapen, setVelgerAapen] = useState(false);
  const notisAvgjort = Boolean(h.conclusionNotice || h.conclusionNoticeSkipped);
  // Lenking krever behandler i Innspill OG en rolle i Hypoteser (kontraktens to krav), og er lov
  // også etter konklusjon og stopp — helt til notisen er sendt eller valgt bort (P2).
  const kanLenke = tilgang.kanSkrive('innspill') && tilgang.kanSe(HYPOTESER) && !notisAvgjort && !['utkast', 'avvist', 'trukket'].includes(h.status);
  const fjernes = p.venter?.overgang.handling === 'removeEvidence' ? p.venter.overgang.submissionId : null;
  // H3.5: lenkene (P10) — og lenken som venter i angre-vinduet står ikke så lenge.
  const lenkerKan = kanEndreLenker(h, megId, rom.behandler || tilgang.eier);
  const fjernesLenke = p.venter?.overgang.handling === 'removeLink' ? p.venter.overgang.linkId : null;
  // «Én side» (P8) finnes fra konklusjonen og stoppet, og ikke før.
  const enSide = (h.status === 'konkludert' || h.status === 'stoppet') && h.number !== undefined ? `/h/registeret/${h.number}/en-side` : null;
  const notatRef = useRef<HTMLTextAreaElement | null>(null);

  // --- H3.4: betaprogrammet fra hypotesen og døren -------------------------------------------
  // Programmer lages i valgt og testes, og blir stående på hypotesen etterpå.
  const kanHaProgram = ['valgt', 'testes', 'pa_pause', 'konkludert', 'stoppet'].includes(h.status);
  const prg = useHypoteseProgrammer(h.id, kanHaProgram);
  const doer = prg.data?.door;
  const prgItems = prg.data?.items ?? [];

  // --- H3.3: hukommelsen, sammenslåing og interessen ---------------------------------------------
  const medinnsender = (h.coSubmitters ?? []).some((c) => c.admin.id === megId);
  const kanSlaaSammen = roller.behandler && (h.status === 'mottatt' || h.status === 'vurderes') && !roller.innsender && !medinnsender;
  const lignende = useSammenslaaingKandidater(h.id, kanSlaaSammen);
  const [slaar, setSlaar] = useState<HypothesisSummary | null>(null);
  const [dataAapen, setDataAapen] = useState(false);
  const varsle = useVarsleMeg();
  const kvitter = useKvitterVarsel();
  // «Testet før — se konklusjonen»: hypotesene den kan ha blitt testet i (konkludert eller stoppet).
  const vurdertFor = ark.valgt === 'avvis' && ark.avvisKode === 'vurdert_for';
  const konkluderte = useHypoteseListe({ statusType: 'complete' }, vurdertFor);
  const stoppede = useHypoteseListe({ status: 'stoppet' }, vurdertFor);
  const tidligere = useMemo(() => {
    const ut = new Map<string, { id: string; tekst: string }>();
    const forrige = h.supersedesCard;
    if (forrige && (forrige.status === 'konkludert' || forrige.status === 'stoppet')) ut.set(forrige.hypothesis.id, { id: forrige.hypothesis.id, tekst: `${nummerTekst(forrige.hypothesis.number)} · ${forrige.hypothesis.title}` });
    for (const x of [...(konkluderte.data?.pages.flatMap((s) => s.items) ?? []), ...(stoppede.data?.pages.flatMap((s) => s.items) ?? [])]) {
      if (x.id !== h.id && !ut.has(x.id)) ut.set(x.id, { id: x.id, tekst: `${nummerTekst(x.number)} · ${x.title ?? ''}` });
    }
    return [...ut.values()];
  }, [h.id, h.supersedesCard, konkluderte.data, stoppede.data]);

  const aapneNotat = useCallback(() => {
    setArk((a) => ({ ...a, notatAapent: true }));
    setTimeout(() => notatRef.current?.focus(), 0);
  }, []);

  // Arket åpnes med leietakerens mal for avgjørelsen i svarfeltet, når feltet er tomt — og en
  // justering med forslag til svar (`proposedReply`) åpnes med forslaget i stedet for malen.
  // «Gi videre» starter fra det som gjelder nå; «Løsne låsen» fra den første låsen.
  const hRef = useRef(h);
  hRef.current = h;
  const velgHandling = useCallback(
    (k: HandlingK | null) =>
      setArk((a) => {
        const hn = hRef.current;
        let neste: Ark = { ...a, valgt: k, kortAapent: false };
        if (k === 'giVidere' && !a.gvEier && !a.gvBesl) neste = { ...neste, gvEier: hn.owner?.id ?? '', gvBesl: hn.decider?.id ?? '' };
        if (k === 'losne' && !a.losneKind) neste = { ...neste, losneKind: loesbareLaaser(hn)[0]?.kind };
        const hvor = k ? MAL_FOR[k] : undefined;
        const forslag = k === 'godkjenn' || k === 'avslaa' ? ventendeJustering(hn)?.proposedReply : undefined;
        if (hvor && forslag !== undefined) return a[hvor.felt] !== '' ? neste : { ...neste, [hvor.felt]: forslag };
        const mal = hvor ? malFor(config, hvor.bruk) : undefined;
        if (!hvor || !mal || a[hvor.felt] !== '') return neste;
        return { ...neste, [hvor.felt]: mal.text, maler: { ...a.maler, [k as MalFelt]: mal.id } };
      }),
    [config],
  );

  // --- Feilen fra tjeneren, lest for mennesker -----------------------------------------
  const feilKode = p.feil?.code;
  const sisteOvergang = h.transitions[h.transitions.length - 1];

  // --- Datoene arkene tar --------------------------------------------------------------
  const iMorgen = leggTilDager(iDag, 1);
  const parkGrense = leggTilMaaneder(iDag, 12);
  const parkUtenfor = ark.parkDato !== '' && (ark.parkDato < iMorgen || ark.parkDato > parkGrense);
  // Fristen som kortes: avgjørelsen i vurderes, konklusjonen i testes (`HypothesisDeadlineKind`).
  const fristKind: 'review' | 'conclusion' = h.status === 'testes' ? 'conclusion' : 'review';
  const fristen = fristKind === 'conclusion' ? h.conclusionDueAt : h.deadlineAt;
  const gjeldendeFrist = fristen ? isoDag(new Date(fristen)) : null;
  const pauseGrense = leggTilMaaneder(iDag, 3);
  const pauseUtenfor = ark.pauseDato !== '' && (ark.pauseDato < iMorgen || ark.pauseDato > pauseGrense);
  const kortSenere = ark.kortDato !== '' && gjeldendeFrist !== null && ark.kortDato >= gjeldendeFrist;
  const kortForTidlig = ark.kortDato !== '' && ark.kortDato <= iDag;

  // --- Avslagskodene: leietakerens etiketter og maler, skjulte koder står ikke ----------
  // Kodens mal først; har koden ingen, leietakerens svarmal for avvist. Begge er låst (DD-09).
  const koder = (config?.rejectReasonWording ?? []).filter((k) => !k.hidden);
  const avvisStart = (kode: RejectReason | undefined): { tekst: string; mal?: SvarMal } => {
    const kodensMal = koder.find((k) => k.code === kode)?.template ?? '';
    if (kodensMal.trim()) return { tekst: kodensMal };
    const svarmal = malFor(config, 'avvist');
    return svarmal ? { tekst: svarmal.text, mal: svarmal } : { tekst: '' };
  };
  const mal = ark.avvisKode ? avvisStart(ark.avvisKode).tekst : '';
  const malUendret = ark.avvisSvar.trim() !== '' && mal.trim() !== '' && ark.avvisSvar.trim() === mal.trim();

  // --- Stoppkodene: samme regel som avslagskodene (DD-09) ---------------------------------
  const stoppKoder = (config?.stopReasonWording ?? []).filter((k) => !k.hidden);
  const stoppStart = (kode: StopReason | undefined): { tekst: string; mal?: SvarMal } => {
    const kodensMal = stoppKoder.find((k) => k.code === kode)?.template ?? '';
    if (kodensMal.trim()) return { tekst: kodensMal };
    const svarmal = malFor(config, 'stoppet');
    return svarmal ? { tekst: svarmal.text, mal: svarmal } : { tekst: '' };
  };
  const stoppMal = ark.stoppKode ? stoppStart(ark.stoppKode).tekst : '';
  const stoppMalUendret = ark.stoppSvar.trim() !== '' && stoppMal.trim() !== '' && ark.stoppSvar.trim() === stoppMal.trim();
  // De obligatoriske radene (DD-35): «Ingen» alene er under minimum — en setning om hvorfor kreves.
  const radMin = config?.minFieldLength ?? 10;
  const radKort = (v: string) => v.trim().length < radMin;

  // --- Etter valgt: justeringen, låsene, eieren og konklusjonen ------------------------------
  const venterJustering = ventendeJustering(h);
  const foerJustering = venterJustering ? revisjonenFoer(h, venterJustering) : undefined;
  const justeringTekst = venterJustering && foerJustering ? endringTekst(endredeLedd(foerJustering.form, venterJustering.form)) : '';
  const laaser = loesbareLaaser(h);
  const deaktivert = eierDeaktivert(h, config);
  // Forslaget til svar (DD-09): utgangspunkt, aldri hele svaret — nektet uendret her, før tjeneren (422 template_unmodified).
  const forslag = venterJustering?.proposedReply;
  const forslagUendret = (v: string) => forslag !== undefined && v.trim() === forslag.trim();
  const FORSLAG_GRUNN = 'Endre forslaget før du sender — det er et utgangspunkt, ikke svaret.';

  // --- Eierkandidatene i «Velg» og «Gi videre»: rommets personer -----------------------------
  const kandidater = useMemo(() => eierkandidater(config), [config]);
  const valgtEier = kandidater.find((k) => k.id === ark.velgEier);
  // Beslutningstakerne i «Gi videre»: lista, aldri innsenderen (422 decider_is_submitter), og den som avgjør nå.
  const beslutterValg = useMemo(() => {
    const ut = new Map<string, AdminUserRef>();
    for (const d of config?.deciders ?? []) if (d.id !== h.submittedBy.id) ut.set(d.id, d);
    if (h.decider && !ut.has(h.decider.id)) ut.set(h.decider.id, h.decider);
    return [...ut.values()];
  }, [config, h.submittedBy.id, h.decider]);
  const gvNyEier = ark.gvEier !== '' && ark.gvEier !== h.owner?.id ? kandidater.find((k) => k.id === ark.gvEier) : undefined;
  const gvNyBesl = ark.gvBesl !== '' && ark.gvBesl !== h.decider?.id ? beslutterValg.find((k) => k.id === ark.gvBesl) : undefined;
  const gvSamme = ark.gvEier !== '' && ark.gvEier === ark.gvBesl;

  // --- Foten: ÉTT primærvalg (DD-58) ----------------------------------------------------
  const venter = p.venter !== null;
  const hold = (overgang: Ventende['overgang'], tekst: string) => p.onHold({ id: h.id, nummer: h.number, overgang, tekst });

  const kvalitetssjekkFerdig = ark.kanBomme !== undefined && ark.kilde !== undefined && ark.baseline !== undefined;
  const taTilVurdering = h.status === 'mottatt' && roller.behandler;
  const nyFrist = config ? kortDatoAv(new Date(Date.now() + config.reviewDays * 86_400_000).toISOString()) : '—';

  let utenFot = false;
  let fot: { label: string; grunn: string | null; onClick: () => void; setning?: string; oppsummering?: string; tast?: 'ctrl-enter' | 'ingen'; testid?: string; ikon?: string; primarFoerst?: boolean; venstreAlltid?: ReactNode };
  if (ark.notatAapent && roller.leserPluss) {
    fot = {
      label: 'Lagre notatet',
      grunn: ark.notat.trim() ? null : 'Skriv notatet først.',
      onClick: () => void lagreNotat(),
      setning: 'Notatet forlater aldri panelet.',
    };
  } else if (ark.kortAapent) {
    fot = {
      label: ark.kortDato ? `Kort fristen til ${kortDato(ark.kortDato)}` : 'Kort fristen',
      grunn: !ark.kortDato ? 'Velg en ny dato først.' : kortSenere ? 'En frist kan bare kortes.' : kortForTidlig ? 'Velg en dato etter i dag.' : null,
      onClick: () => void kortFristen(),
      setning: ark.kortDato ? `${stor(innsender)} ser: «${fristKind === 'conclusion' ? 'Konklusjonsfristen' : 'Fristen'} ble kortet til ${kortDato(ark.kortDato)} av ${p.megNavn ?? 'deg'}.»` : undefined,
    };
  } else if (ark.valgt === 'tm') {
    fot = {
      label: 'Send spørsmålet',
      grunn: ark.tm.trim().length < TM_MIN || harMalPlass(ark.tm) ? `Skriv spørsmålet først — minst ${TM_MIN} tegn.` : null,
      onClick: () => hold({ handling: 'needsMore', body: svarMed(ark.tm, ark.maler.tm) }, `Spørsmålet til ${innsenderFor} sendes når tiden er ute.`),
      setning: `${stor(innsender)} får spørsmålet. Fristen står${h.deadlineAt ? `: ${kortDatoAv(h.deadlineAt)}` : ''}.`,
      oppsummering: `${statusNavn(config, h)} · venter på innsender`,
    };
  } else if (ark.valgt === 'park') {
    const datoTekst = ark.parkDato ? datoMedAar(ark.parkDato, iDag) : '';
    fot = {
      label: datoTekst ? `Parker til ${datoTekst}` : 'Parker',
      grunn: !ark.parkDato ? 'Velg datoen den tas opp igjen.' : parkUtenfor ? `Velg en dato fra i morgen til ${datoMedAar(parkGrense, iDag)}.` : !ark.parkSvar.trim() || harMalPlass(ark.parkSvar) ? 'Skriv grunnen før du parkerer.' : null,
      onClick: () => hold({ handling: 'park', body: { revisitAt: ark.parkDato, response: svarMed(ark.parkSvar, ark.maler.park) } },`${nr} parkeres til ${datoTekst}. Svaret til ${innsenderFor} sendes når tiden er ute.`),
      setning: `${stor(innsenderFor)} får: «${loefteVed(config, h, { status: 'parkert', revisitAt: ark.parkDato || undefined }) ?? ''}»`,
      oppsummering: `→ ${statusNavn(config, { status: 'parkert' })} · svaret sendes`,
    };
  } else if (ark.valgt === 'velg') {
    const eierErMeg = ark.velgEier !== '' && ark.velgEier === megId;
    fot = {
      label: valgtEier && !eierErMeg ? `Velg — ${fornavn(valgtEier)} eier` : 'Velg',
      grunn: roller.innsender
        ? 'Du sendte den inn selv — en annen beslutningstaker avgjør.'
        : !ark.velgEier
          ? 'Velg en eier først.'
          : eierErMeg
            ? 'Velg en annen eier enn deg.'
            : !ark.velgSvar.trim() || harMalPlass(ark.velgSvar)
              ? `Skriv svaret til ${innsenderFor} først.`
              : null,
      onClick: () => hold({ handling: 'select', body: { ownerId: ark.velgEier, response: svarMed(ark.velgSvar, ark.maler.velg) } },`${nr} velges — ${valgtEier ? navnAv(valgtEier) : ''} eier. Svaret til ${innsenderFor} sendes når tiden er ute.`),
      setning: valgtEier && !eierErMeg ? `${stor(oppramsingNavn([h.reviewer ? fornavn(h.reviewer) : null, fornavn(valgtEier), innsenderFor]))} får beskjed.` : undefined,
      oppsummering: `→ ${statusNavn(config, { status: 'valgt' })} · svaret sendes`,
    };
  } else if (ark.valgt === 'avvis') {
    fot = {
      label: 'Send avslag',
      grunn: !ark.avvisKode
        ? 'Velg en grunn for avslaget først.'
        : vurdertFor && !ark.avvisRelatert
          ? 'Velg hypotesen den ble testet i.'
          : !ark.avvisSvar.trim() || malUendret || harMalPlass(ark.avvisSvar)
            ? 'Endre malen før du sender.'
            : null,
      onClick: () =>
        hold(
          { handling: 'reject', body: { reasonCode: ark.avvisKode!, response: svarMed(ark.avvisSvar, ark.maler.avvis), ...(vurdertFor ? { relatedHypothesisId: ark.avvisRelatert } : {}) } },
          `${nr} avvises. Svaret til ${innsenderFor} sendes når tiden er ute.`,
        ),
      setning: `${stor(innsender)} får avslaget med begrunnelsen.`,
      oppsummering: `→ ${statusNavn(config, { status: 'avvist' })} · svaret sendes`,
    };
  } else if (ark.valgt === 'unpark') {
    const frist = config ? kortDato(leggTilDager(iDag, config.reviewDays)) : '—';
    fot = {
      label: 'Ta tilbake til vurdering',
      grunn: null,
      onClick: () => hold({ handling: 'unpark', body: ark.unparkSvar.trim() ? { response: { text: ark.unparkSvar.trim() } } : {} }, `${nr} tas tilbake til vurdering. Fristen blir ${frist}.`),
      setning: `Fristen blir ${frist}. ${stor(innsender)} får beskjed.`,
      oppsummering: `→ ${statusNavn(config, { status: 'vurderes' })}`,
    };
  } else if (ark.valgt === 'godkjenn' && venterJustering) {
    fot = {
      label: `Godkjenn — versjon ${venterJustering.revision} gjelder`,
      grunn: !ark.godkjennSvar.trim() || harMalPlass(ark.godkjennSvar) ? `Skriv svaret til ${innsenderFor} først.` : forslagUendret(ark.godkjennSvar) ? FORSLAG_GRUNN : null,
      onClick: () => hold({ handling: 'approveAdjustment', body: { response: svarMed(ark.godkjennSvar, ark.maler.godkjenn) } }, `Justeringen godkjennes. Svaret til ${innsenderFor} sendes når tiden er ute.`),
      setning: `${stor(innsender)} får svaret. ${h.owner?.id === megId ? 'Du' : stor(fornavn(h.owner))} kan starte.`,
      oppsummering: `${statusNavn(config, { status: 'valgt' })} · svaret sendes`,
    };
  } else if (ark.valgt === 'avslaa' && venterJustering) {
    fot = {
      label: `Avslå — versjon ${foerJustering?.revision ?? '—'} gjelder`,
      grunn: !ark.avslaaSvar.trim() || harMalPlass(ark.avslaaSvar) ? `Skriv hvorfor til ${innsenderFor} først.` : forslagUendret(ark.avslaaSvar) ? FORSLAG_GRUNN : null,
      onClick: () => hold({ handling: 'declineAdjustment', body: { response: svarMed(ark.avslaaSvar, ark.maler.avslaa) } }, `Justeringen avslås. Svaret til ${innsenderFor} sendes når tiden er ute.`),
      setning: `${stor(innsender)} får svaret. ${stor(fornavn(h.reviewer))} eller ${fornavn(h.owner)} kan foreslå en ny justering.`,
      oppsummering: `${statusNavn(config, { status: 'valgt' })} · svaret sendes`,
    };
  } else if (ark.valgt === 'losne') {
    fot = {
      label: 'Løsne låsen',
      grunn: !ark.losneKind ? 'Velg låsen først.' : ark.losneGrunn.trim().length < 20 ? 'Skriv grunnen — minst 20 tegn.' : null,
      onClick: () => void direkteMed({ handling: 'override', body: { kind: ark.losneKind!, reason: ark.losneGrunn.trim() } }, 'Låsen er løsnet. Grunnen står i loggen.'),
      setning: 'Grunnen står i loggen med ditt navn og datoen.',
    };
  } else if (ark.valgt === 'giVidere') {
    const body = { ...(gvNyEier ? { ownerId: gvNyEier.id } : {}), ...(gvNyBesl ? { deciderId: gvNyBesl.id } : {}) };
    fot = {
      label: 'Gi videre',
      grunn: gvSamme ? 'Eieren og beslutningstakeren må være to personer.' : !gvNyEier && !gvNyBesl ? 'Velg en ny eier eller en ny beslutningstaker.' : null,
      onClick: () => void direkteMed({ handling: 'assign', body }, `${nr} er gitt videre.`),
      setning: gvNyEier ? `${stor(fornavn(gvNyEier))} må ta eierskapet ${h.status === 'valgt' ? 'før testen kan starte' : 'på nytt'}.` : gvNyBesl ? `${stor(fornavn(gvNyBesl))} avgjør fra nå.` : undefined,
    };
  } else if (ark.valgt === 'kilde') {
    fot = {
      label: 'Kilden er verifisert',
      grunn: null,
      onClick: () => void direkteMed({ handling: 'verifySource' }, 'Kilden er verifisert. Navnet ditt og datoen står i loggen.'),
      setning: 'Låsen på kilden går. Navnet ditt og datoen står i loggen.',
    };
  } else if (ark.valgt === 'maaling') {
    fot = {
      label: 'Målingen er klar',
      grunn: null,
      onClick: () => void direkteMed({ handling: 'measurementReady' }, 'Målingen er meldt klar. Kilden må fortsatt verifiseres.'),
      setning: 'Kilden må fortsatt verifiseres før testen kan starte.',
    };
  } else if (ark.valgt === 'pause') {
    const datoTekst = ark.pauseDato ? datoMedAar(ark.pauseDato, iDag) : '';
    fot = {
      label: datoTekst ? `Sett på pause til ${datoTekst}` : 'Sett på pause',
      grunn: !ark.pauseDato ? 'Velg datoen den avgjøres.' : pauseUtenfor ? `Velg en dato fra i morgen til ${datoMedAar(pauseGrense, iDag)}.` : !ark.pauseSvar.trim() || harMalPlass(ark.pauseSvar) ? 'Skriv hvorfor først.' : null,
      onClick: () => hold({ handling: 'pause', body: { revisitAt: ark.pauseDato, response: svarMed(ark.pauseSvar, ark.maler.pause) } }, `${nr} settes på pause til ${datoTekst}. Svaret til ${innsenderFor} sendes når tiden er ute.`),
      setning: `${stor(innsenderFor)} får: «${loefteVed(config, h, { status: 'pa_pause', revisitAt: ark.pauseDato || undefined }) ?? ''}»`,
      oppsummering: `→ ${statusNavn(config, { status: 'pa_pause' })}`,
    };
  } else if (ark.valgt === 'fortsett') {
    const tilbakeTil = foerPausen(h);
    const andreF = h.owner?.id === megId ? navnAv(h.decider) : navnAv(h.owner);
    fot = {
      label: tilbakeTil === 'testes' ? 'Fortsett testen' : 'Fortsett',
      grunn: null,
      onClick: () => hold({ handling: 'resume', body: {} }, `${nr} fortsetter. ${stor(innsenderFor)} får beskjed når tiden er ute.`),
      setning: `${stor(innsender)} og ${andreF} får beskjed.`,
      oppsummering: `→ ${statusNavn(config, { status: tilbakeTil })}`,
    };
  } else if (ark.valgt === 'stopp') {
    fot = {
      label: 'Stopp testen',
      grunn: !ark.stoppKode
        ? 'Velg en stoppkode først.'
        : ark.stoppLaerte.trim().length < 40
          ? 'Skriv hva dere rakk å lære — minst 40 tegn.'
          : radKort(ark.stoppBlokk) || radKort(ark.stoppData)
            ? 'Fyll ut begge radene — «Ingen» med en setning om hvorfor.'
            : !ark.stoppSvar.trim() || stoppMalUendret || harMalPlass(ark.stoppSvar)
              ? 'Endre malen før du sender.'
              : null,
      onClick: () =>
        hold(
          { handling: 'stop', body: { stopReason: ark.stoppKode!, learned: ark.stoppLaerte.trim(), openBlocking: ark.stoppBlokk.trim(), dataLimitations: ark.stoppData.trim(), response: svarMed(ark.stoppSvar, ark.maler.stopp) } },
          `${nr} stoppes. Svaret til ${innsenderFor} sendes når tiden er ute.`,
        ),
      setning: `${stor(innsenderFor)} får: «${loefteVed(config, h, { status: 'stoppet' }) ?? ''}»`,
      oppsummering: `→ ${statusNavn(config, { status: 'stoppet' })} · svaret sendes`,
    };
  } else if (ark.valgt === 'bekreft' && h.conclusion?.outcome) {
    fot = {
      label: 'Bekreft — konkludert',
      grunn: !ark.bekreftSvar.trim() || harMalPlass(ark.bekreftSvar) ? `Skriv svaret til ${innsenderFor} først.` : null,
      onClick: () =>
        hold(
          { handling: 'confirmConclusion', body: { response: svarMed(ark.bekreftSvar, ark.maler.bekreft) } },
          `${nr} konkluderes: ${statusNavn(config, { status: 'konkludert', outcome: h.conclusion!.outcome }).toLowerCase()}. Svaret til ${innsenderFor} sendes når tiden er ute.`,
        ),
      setning: 'Konklusjonen fryses. En rettelse etterpå er et funn.',
      oppsummering: `→ ${statusNavn(config, { status: 'konkludert', outcome: h.conclusion.outcome })} · svaret sendes`,
    };
  } else if (ark.valgt === 'tilbake') {
    fot = {
      label: `Send tilbake til ${fornavn(h.owner)}`,
      grunn: !ark.tilbakeMerknad.trim() ? 'Skriv merknaden først.' : null,
      onClick: () => void direkteMed({ handling: 'sendBackConclusion', body: { note: ark.tilbakeMerknad.trim() } }, `Konklusjonen er sendt tilbake til ${fornavn(h.owner)}.`),
      setning: `Ingen overgang. ${stor(innsenderFor)} får ingenting; ${fornavn(h.owner)} skriver på nytt.`,
    };
  } else if (taTilVurdering) {
    fot = {
      label: 'Ta til vurdering',
      grunn: roller.innsender ? 'Du kan ikke vurdere din egen hypotese.' : !kvalitetssjekkFerdig ? 'Velg alle tre vurderingene først.' : null,
      onClick: () => void startVurdering(),
      setning: `Fristen blir ${nyFrist}. ${stor(innsender)} får vite at du vurderer.`,
      oppsummering: `→ ${statusNavn(config, { status: 'vurderes' })}`,
    };
  } else if (notisSkrivesAv(h, megId)) {
    // H3.1, skjerm 11 og 14: «Send til N» fylt, «Ingen notis» som tekstknapp, og hvem som får den.
    // Begge er endelige og holdes tilbake i angre-vinduet, som overgangene.
    const min = config?.minFieldLength ?? 10;
    const t = ark.notis.trim();
    const n = h.evidenceReach;
    fot = {
      label: `Send til ${n}`,
      ikon: 'send-outline',
      grunn: t === '' ? 'Skriv notisen først.' : t.length < min ? `Notisen må ha minst ${min} tegn.` : null,
      onClick: () => hold({ handling: 'sendNotice', body: { text: t } }, `Notisen sendes til ${n} når tiden er ute.`),
      primarFoerst: true,
      venstreAlltid: (
        <span className="hb-notis-fot">
          <button type="button" className="panel-knapp-lenke hb-noeytral" onClick={() => hold({ handling: 'skipNotice' }, `Ingen notis til de ${n} — lagres når tiden er ute.`)} data-testid="hb-ingen-notis">
            Ingen notis
          </button>
          <span className="hb-notis-fot-setning">{smal ? 'Også de som alt har fått svar.' : 'Også de som alt har fått svar på innspillet sitt.'}</span>
        </span>
      ),
    };
  } else {
    // Ingenting å lagre: ingen fot (DD-58, ett primærvalg — aldri en fot for fotens skyld). Konseptsiden
    // tegner valgt, testes og konkludert uten fot når den innloggede ikke har et valg åpent.
    utenFot = true;
    fot = { label: 'Lagre', grunn: 'Ingen endringer ennå', onClick: () => undefined, tast: 'ctrl-enter' };
  }
  // H3.3 (M6): under 900 er foten tommelsonen — «Jeg har data» er dens ene fylte knapp, og
  // «Varsle meg» står ved siden av som omriss. Over 900 står begge i seksjonen (W14).
  const giData = kanGiData(h) && !(h.dataPointers ?? []).some((d) => d.by.id === megId);
  const interesseIFoten = utenFot && smal && giData;
  if (interesseIFoten) {
    utenFot = false;
    fot = {
      label: 'Jeg har data',
      grunn: null,
      onClick: () => setDataAapen(true),
      tast: 'ingen',
      ikon: 'bar-chart-outline',
      venstreAlltid:
        kanFoelges(h.statusType, h.status) && !h.watching ? (
          <button type="button" className="panel-knapp-sekundaer" onClick={() => varsle.mutate({ id: h.id, paa: true })}>
            <Ikon navn="notifications-outline" />
            Varsle meg
          </button>
        ) : (
          <span />
        ),
    };
  }
  if (venter) {
    utenFot = false;
    fot = { ...fot, grunn: null, label: 'Sendes når tiden er ute', onClick: () => undefined, venstreAlltid: undefined };
  }

  async function startVurdering() {
    if (!kvalitetssjekkFerdig || roller.innsender) return;
    const check: ReviewCheck = { canFail: ark.kanBomme!, sourceCheck: ark.kilde!, baselineCheck: ark.baseline!, ...(ark.merknad.trim() ? { canFailNote: ark.merknad.trim() } : {}) };
    try {
      await direkte.mutateAsync({ id: h.id, overgang: { handling: 'startReview', body: { reviewCheck: check } } });
      glemArk(h.id);
      p.onKvittering(`${nr} → ${statusNavn(config, { status: 'vurderes' })}. ${stor(innsenderFor)} får vite at du vurderer.`);
    } catch (e) {
      p.onFeil(e);
    }
  }

  async function kortFristen() {
    if (!ark.kortDato || kortSenere || kortForTidlig) return;
    try {
      await direkte.mutateAsync({ id: h.id, overgang: { handling: 'shortenDeadline', body: { kind: fristKind, toAt: tidspunktFor(ark.kortDato) } } });
      setArk((a) => ({ ...a, kortAapent: false, kortDato: '' }));
      p.onKvittering(`${fristKind === 'conclusion' ? 'Konklusjonsfristen' : 'Fristen'} for ${nr} er kortet til ${kortDato(ark.kortDato)}.`);
    } catch (e) {
      p.onFeil(e);
    }
  }

  /**
   * Et steg uten svar til innsenderen sendes straks (ingen angre-vindu): «Gi videre», «Løsne
   * låsen», «Kilden er verifisert», «Målingen er klar» og «Send tilbake med merknad». Arket
   * lukkes; det som ble skrevet, er sendt. «Juster før start» er en egen side (`JusterSide`).
   */
  async function direkteMed(overgang: Overgang, kvittering: string) {
    try {
      await direkte.mutateAsync({ id: h.id, overgang });
      glemArk(h.id);
      setArk((a) => ({ ...TOMT_ARK, notat: a.notat, notatAapent: a.notatAapent }));
      p.onKvittering(kvittering);
    } catch (e) {
      p.onFeil(e);
    }
  }

  async function lagreNotat() {
    const tekst = ark.notat.trim();
    if (!tekst) return;
    try {
      await leggTilNotat.mutateAsync({ id: h.id, text: tekst });
      setArk((a) => ({ ...a, notat: '', notatAapent: false }));
      p.onKvittering('Notatet er lagret. Det forlater aldri panelet.');
    } catch (e) {
      p.onFeil(e);
    }
  }

  // --- Tastene i detaljen (DD-11 presisert 2026-09-26) ---------------------------------
  const akkord = useRef<number | null>(null);
  const handlingerRef = useRef(handlinger);
  handlingerRef.current = handlinger;
  const lagreNotatRef = useRef(lagreNotat);
  lagreNotatRef.current = lagreNotat;
  const foersteKandidat = useRef<HypothesisSummary | undefined>(undefined);
  foersteKandidat.current = kanSlaaSammen && !slaar ? lignende.data?.[0] : undefined;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const maal = e.target as HTMLElement | null;
      const iFelt = maal !== null && /^(INPUT|TEXTAREA|SELECT)$/.test(maal.tagName);
      if ((e.key === 's' || e.key === 'S') && (e.ctrlKey || e.metaKey) && !e.altKey) {
        if (!arkRef.current.notatAapent) return;
        e.preventDefault();
        void lagreNotatRef.current();
        return;
      }
      if (e.key === 'Escape') {
        // Esc lukker arket — aldri siden. Teksten står til arket åpnes igjen.
        const a = arkRef.current;
        if (a.valgt || a.kortAapent || a.notatAapent) {
          if (iFelt) maal!.blur();
          setArk((x) => (x.valgt ? { ...x, valgt: null } : x.kortAapent ? { ...x, kortAapent: false } : { ...x, notatAapent: false }));
        }
        return;
      }
      if (iFelt || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (akkord.current && Date.now() - akkord.current < 1500) {
        akkord.current = null;
        const treff = handlingerRef.current.find((x) => x.tast !== undefined && x.tast.toLowerCase() === e.key.toLowerCase());
        if (treff) {
          e.preventDefault();
          velgHandling(treff.k);
        }
        return;
      }
      if (e.key === 's') {
        akkord.current = Date.now();
        return;
      }
      // H3.3: «Slå sammen» med den første som ligner (panelkonseptets M på raden).
      if (e.key === 'm' && foersteKandidat.current) {
        e.preventDefault();
        setSlaar(foersteKandidat.current);
        return;
      }
      if (e.key === 'n' && rom.leserPluss) {
        e.preventDefault();
        aapneNotat();
        return;
      }
      // H3.5: E åpner «Én side» i ny fane, for en konkludert eller stoppet hypotese.
      const hn = hRef.current;
      if (e.key === 'e' && (hn.status === 'konkludert' || hn.status === 'stoppet') && hn.number !== undefined) {
        e.preventDefault();
        window.open(`/h/registeret/${hn.number}/en-side`, '_blank', 'noopener');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [aapneNotat, velgHandling, rom.leserPluss]);

  // --- Banneret: tjeneren kom først -----------------------------------------------------
  let banner: ReactNode = null;
  if (feilKode === 'invalid_status_transition') {
    const hvaSkjedde =
      sisteOvergang && sisteOvergang.by.id !== megId
        ? `${stor(navnAv(sisteOvergang.by))} ${overgangOrd(sisteOvergang.toStatus)} kl. ${klokke(sisteOvergang.at)}, mens du skrev.`
        : 'Noen andre endret hypotesen mens du skrev.';
    banner = (
      <Varsel tone="info" rolle="status" handling={p.onNeste ? <Tekstknapp onClick={p.onNeste}>Neste i lista</Tekstknapp> : undefined}>
        Det du gjorde ble ikke sendt. {hvaSkjedde} Det du skrev står under.
      </Varsel>
    );
  } else if (feilKode === 'own_hypothesis' || feilKode === 'decider_is_submitter') {
    banner = (
      <Varsel tone="info" rolle="status">
        Du sendte den inn selv. En annen tar avgjørelsen — ingenting ble sendt.
      </Varsel>
    );
  } else if (feilKode === 'not_decider' && ark.kortAapent) {
    // Feilen står ved datofeltet («Kort fristen»), ikke en gang til i et banner.
    banner = null;
  } else if (feilKode === 'not_decider' && (h.status === 'valgt' || h.status === 'testes' || h.status === 'pa_pause')) {
    banner = (
      <Varsel tone="info" rolle="status">
        Du er ikke lenger beslutningstaker på denne, så det ble ikke sendt. Det du skrev står under.
      </Varsel>
    );
  } else if (feilKode === 'not_decider') {
    banner = (
      <Varsel tone="info" rolle="status">
        Du står ikke på lista over beslutningstakere lenger, så avgjørelsen ble ikke sendt. Det du skrev står under.
      </Varsel>
    );
  } else if (feilKode === 'not_owner' || feilKode === 'not_reviewer') {
    banner = (
      <Varsel tone="info" rolle="status">
        Rollen din på hypotesen har endret seg, så det ble ikke sendt. Det du skrev står under.
      </Varsel>
    );
  } else if (deaktivert && (h.status === 'valgt' || h.status === 'testes' || h.status === 'pa_pause')) {
    // Eieren er deaktivert: beslutningstakeren gir den videre (DD-08: ikon og ord, aldri bare farge).
    banner = (
      <Varsel>
        {navnAv(h.owner)} er deaktivert. Gi hypotesen til en ny eier{h.status === 'valgt' ? ' før testen kan starte' : ''}.
      </Varsel>
    );
  }

  // P10b: det usendte står under banneret når arket ikke lenger finnes i statusen hypotesen fikk.
  const USENDT: Partial<Record<HandlingK, keyof Ark>> = {
    tm: 'tm',
    park: 'parkSvar',
    velg: 'velgSvar',
    avvis: 'avvisSvar',
    unpark: 'unparkSvar',
    godkjenn: 'godkjennSvar',
    avslaa: 'avslaaSvar',
    losne: 'losneGrunn',
    pause: 'pauseSvar',
    stopp: 'stoppSvar',
    bekreft: 'bekreftSvar',
    tilbake: 'tilbakeMerknad',
  };
  const usendtNoekkel: keyof Ark | null = (ark.valgt && USENDT[ark.valgt]) || null;
  const arketFinnes = ark.valgt !== null && handlinger.some((x) => x.k === ark.valgt);
  const usendt =
    feilKode === 'invalid_status_transition' && usendtNoekkel && !arketFinnes && String(ark[usendtNoekkel]).trim() ? (
      <Felt id="hb-usendt" etikett="Det du skrev · bare du ser det">
        <textarea id="hb-usendt" className="h-input h-tekstfelt" rows={2} value={String(ark[usendtNoekkel])} onChange={(e) => sett(usendtNoekkel, e.target.value as never)} />
      </Felt>
    ) : null;

  // --- Sporingslinjen (DD-56 presisert, DD-28) ------------------------------------------
  const spor = sporFor(h, config, megId, roller.behandler);

  // --- «Nå» -------------------------------------------------------------------------------
  const noekkelNavn = statusNavn(config, h);
  const loefte = loefteFor(config, h);
  const pauset = h.status === 'pa_pause' ? [...h.transitions].reverse().find((t) => t.toStatus === 'pa_pause') : undefined;
  const ved =
    h.status === 'mottatt'
      ? 'ingen har tatt den ennå'
      : h.status === 'vurderes'
        ? h.awaitingSubmitter
          ? 'venter på innsender'
          : `${hvem(h.reviewer, megId, 'du') === 'du' ? 'du vurderer' : `${navnAv(h.reviewer)} vurderer`}`
        : h.status === 'parkert'
          ? `Parkert ${h.parkCount} ${h.parkCount === 1 ? 'gang' : 'ganger'}`
          : h.status === 'pa_pause' && pauset
            ? `${hvem(pauset.by, megId, 'du')} ${kortDatoAv(pauset.at)}`
            : h.status === 'slaatt_sammen' && h.mergedInto
              ? `følges videre i ${nummerTekst(h.mergedInto.number)}`
              : null;
  const naaVarsel =
    h.deadlineState === 'avgjoer_naa'
      ? h.status === 'parkert' || h.status === 'pa_pause'
        ? 'Datoen er i dag — den flytter seg ikke av seg selv.'
        : 'Fristen er i dag.'
      : h.deadlineState === 'forsinket'
        ? h.status === 'parkert' || h.status === 'pa_pause'
          ? `Datoen var ${h.revisitAt ? kortDato(h.revisitAt) : '—'}. Den flytter seg ikke av seg selv.`
          : `Fristen var ${h.deadlineAt ? kortDatoAv(h.deadlineAt) : '—'}.`
        : null;
  // Korting (shorten-deadline, 2026-10-08): avgjørelsesfristen er vurdererens når hun er behandler
  // i rommet; konklusjonsfristen er vurdererens som behandler, og hypotesens beslutningstaker
  // uansett rom-rolle — også en bidragsyter. Tjeneren avgjør uansett (403 not_decider).
  const kanKorte =
    (h.status === 'vurderes' && roller.vurderer && roller.behandler && h.deadlineAt !== undefined) ||
    (h.status === 'testes' && ((roller.vurderer && roller.behandler) || roller.avgjoer) && h.conclusionDueAt !== undefined);
  const dagerIgjen = fristen ? dagerTil(fristen) : null;

  const naa = (
    <div className="hb-naa">
      <span className="h-etikett">Nå</span>
      <div className="hb-naa-pille">
        <HypotesePille noekkel={h.status === 'konkludert' && h.outcome ? h.outcome : h.status} navn={noekkelNavn} />
        {ved ? <span className="hb-dempet">{ved}</span> : null}
      </div>
      {loefte ? (
        <p className="hb-dempet">
          {roller.innsender ? 'Du' : innsender} har fått: {loefte}
        </p>
      ) : null}
      {naaVarsel ? (
        <p className="hb-varsel hb-fet">
          <Ikon navn="warning-outline" />
          {naaVarsel}
        </p>
      ) : null}
      {(h.status === 'vurderes' || h.status === 'testes') && fristen ? (
        <div className="hb-frist">
          <span>
            {fristKind === 'conclusion' ? 'Konklusjonsfrist' : 'Frist'} {kortDatoAv(fristen)}
            {dagerIgjen !== null && dagerIgjen > 0 ? ` · ${dagerIgjen} ${dagerIgjen === 1 ? 'dag' : 'dager'} igjen` : ''}
          </span>
          {kanKorte ? (
            <button type="button" className="panel-knapp-lenke" aria-pressed={ark.kortAapent} onClick={() => setArk((a) => ({ ...a, kortAapent: !a.kortAapent, valgt: null }))} data-testid="hb-kort-fristen">
              Kort fristen …
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  // --- «Neste steg» (DD-56) ------------------------------------------------------------------
  const nesteTittel =
    handlinger.length > 0 || taTilVurdering
      ? (roller.beslutter && !roller.vurderer && handlinger.some((x) => x.k === 'velg' || x.k === 'avvis')) || roller.avgjoer
        ? 'Neste steg — din avgjørelse'
        : 'Neste steg — ditt'
      : 'Neste steg';
  const neste =
    handlinger.length > 0 || andre || doer || ark.kortAapent || (h.status === 'vurderes' && roller.vurderer && h.needsMoreCount >= MAKS_TRENGER_MER) ? (
      <div className="hb-neste">
        <span className="h-etikett">{nesteTittel}</span>
        {handlinger.length > 0 ? (
          <div className="hb-handlinger">
            {handlinger.map((x) => (
              <HandlingKnapp key={x.k} x={x} valgt={ark.valgt === x.k} smal={smal} til={x.k === 'juster' ? p.justerTil : x.k === 'start' ? `/h/${h.number}/start` : x.k === 'konkluder' ? `/h/${h.number}/konklusjon` : undefined} onVelg={() => velgHandling(ark.valgt === x.k ? null : x.k)} />
            ))}
          </div>
        ) : null}
        {/* H3.4: døren åpner noe annet uten å endre status — en lenke under stegene, ikke en overgang (DD-56). */}
        {doer ? (
          <div className="hb-ekstra">
            <ProgramDoer h={h} door={doer} harProgram={prgItems.length > 0} />
          </div>
        ) : null}
        {andre ? <p className="hb-dempet">{andre}</p> : null}
        {h.status === 'vurderes' && roller.vurderer && h.needsMoreCount >= MAKS_TRENGER_MER ? (
          <span className="h-grunn" data-testid="hb-grense">
            <Ikon navn="lock-closed-outline" className="h-grunn-ikon" />
            <span>Tre spørsmål er brukt — neste steg er en avgjørelse.</span>
          </span>
        ) : null}
        {ark.kortAapent ? (
          <div className="hb-ark" role="group" aria-label={fristKind === 'conclusion' ? 'Kort konklusjonsfristen' : 'Kort fristen'}>
            <Felt
              id="hb-kort"
              etikett={fristKind === 'conclusion' ? 'Ny konklusjonsfrist' : 'Ny frist'}
              grunn={
                feilKode === 'not_decider'
                  ? 'Du er ikke lenger beslutningstaker på denne — fristen ble ikke kortet.'
                  : kortSenere || feilKode === 'deadline_cannot_be_extended'
                    ? `En frist kan bare kortes. Velg en dato før ${gjeldendeFrist ? kortDato(gjeldendeFrist) : 'fristen'}.`
                    : kortForTidlig
                      ? 'Velg en dato etter i dag.'
                      : null
              }
            >
              <input id="hb-kort" type="date" className="h-input h-input-dato" value={ark.kortDato} min={iMorgen} max={gjeldendeFrist ? leggTilDager(gjeldendeFrist, -1) : undefined} aria-invalid={kortSenere || kortForTidlig || undefined} onChange={(e) => sett('kortDato', e.target.value)} />
            </Felt>
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'tm' ? (
          <div className="hb-ark" role="group" aria-label="Trenger mer">
            <Felt id="hb-tm" etikett={`Spørsmålet til ${innsender}`} teller={`${h.needsMoreCount + 1} av ${MAKS_TRENGER_MER} etter dette`}>
              <textarea id="hb-tm" className="h-input h-tekstfelt" rows={3} maxLength={SVAR_MAKS} value={ark.tm} onChange={(e) => sett('tm', e.target.value)} />
            </Felt>
            <p className="hb-dempet">{innsenderFor} svarer ved å endre hypotesen. Fristen løper imens.</p>
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'park' ? (
          <div className="hb-ark" role="group" aria-label="Parker til">
            <Felt id="hb-park-dato" etikett="Tas opp igjen" grunn={parkUtenfor || feilKode === 'revisit_date_out_of_range' ? `Velg en dato fra i morgen til ${datoMedAar(parkGrense, iDag)}.` : null}>
              <input id="hb-park-dato" type="date" className="h-input h-input-dato" value={ark.parkDato} min={iMorgen} max={parkGrense} aria-invalid={parkUtenfor || undefined} onChange={(e) => sett('parkDato', e.target.value)} />
            </Felt>
            <SvarFelt id="hb-park-svar" mottaker={innsender} verdi={ark.parkSvar} onEndre={(v) => sett('parkSvar', v)} />
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'velg' ? (
          <div className="hb-ark" role="group" aria-label="Velg">
            <Felt id="hb-velg-eier" etikett="Eier av testen · en annen enn deg" grunn={ark.velgEier !== '' && ark.velgEier === megId ? 'Eieren må være en annen enn deg — du blir beslutningstaker.' : feilKode === 'owner_is_decider' ? 'Eieren må være en annen enn deg — du blir beslutningstaker.' : null}>
              <select id="hb-velg-eier" className="h-input" value={ark.velgEier} onChange={(e) => sett('velgEier', e.target.value)}>
                <option value="">Velg eier</option>
                {kandidater.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.id === megId ? megSelv(k) : navnAv(k)}
                  </option>
                ))}
              </select>
            </Felt>
            {valgtEier && ark.velgEier !== megId ? <p className="hb-dempet">{stor(fornavn(valgtEier))} tar eierskapet selv før testen kan starte. Du blir hypotesens beslutningstaker.</p> : null}
            <SvarFelt id="hb-velg-svar" mottaker={innsender} verdi={ark.velgSvar} onEndre={(v) => sett('velgSvar', v)} />
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'avvis' ? (
          <div className="hb-ark" role="group" aria-labelledby="hb-avvis-h">
            <span id="hb-avvis-h" className="h-etikett">
              Hvorfor avvises den?
            </span>
            {koder.length > 0 ? (
              <Radiogruppe
                etikettId="hb-avvis-h"
                valg={koder.map((k) => [k.code, k.label] as [RejectReason, string])}
                aktiv={ark.avvisKode}
                onVelg={(k) => {
                  const start = avvisStart(k);
                  // «Testet før»: den innsenderen bygger på, er valgt når den er konkludert eller stoppet.
                  const forrige = h.supersedesCard && (h.supersedesCard.status === 'konkludert' || h.supersedesCard.status === 'stoppet') ? h.supersedesCard.hypothesis.id : '';
                  setArk((a) => ({ ...a, avvisKode: k, avvisSvar: start.tekst, maler: { ...a.maler, avvis: start.mal?.id }, avvisRelatert: k === 'vurdert_for' ? a.avvisRelatert || forrige : '' }));
                }}
                testid="hb-avvis-koder"
              />
            ) : (
              <span className="h-grunn">
                <Ikon navn="lock-closed-outline" className="h-grunn-ikon" />
                <span>Dere har ingen avslagskoder ennå. Eieren setter dem opp.</span>
              </span>
            )}
            {vurdertFor ? (
              <Felt id="hb-avvis-relatert" etikett="Hvilken ble den testet i?" grunn={feilKode === 'related_hypothesis_required' ? 'Velg en konkludert eller stoppet hypotese.' : null}>
                <select id="hb-avvis-relatert" className="h-input" value={ark.avvisRelatert} onChange={(e) => sett('avvisRelatert', e.target.value)}>
                  <option value="">Velg hypotesen</option>
                  {tidligere.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.tekst}
                    </option>
                  ))}
                </select>
              </Felt>
            ) : null}
            {ark.avvisKode ? (
              <SvarFelt
                id="hb-avvis-svar"
                mottaker={innsender}
                malNavn={koder.find((k) => k.code === ark.avvisKode)?.label}
                verdi={ark.avvisSvar}
                onEndre={(v) => sett('avvisSvar', v)}
                grunn={malUendret || feilKode === 'template_unmodified' ? 'Endre malen før du sender.' : null}
              />
            ) : null}
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'unpark' ? (
          <div className="hb-ark" role="group" aria-label="Ta tilbake til vurdering">
            <p className="hb-dempet">Ny frist fra i dag: {config ? kortDato(leggTilDager(iDag, config.reviewDays)) : '—'}.</p>
            <Felt id="hb-unpark-svar" etikett={`Svar til ${innsender} · valgfritt`} teller={`${ark.unparkSvar.length} / ${SVAR_MAKS}`}>
              <textarea id="hb-unpark-svar" className="h-input h-tekstfelt" rows={2} maxLength={SVAR_MAKS} value={ark.unparkSvar} onChange={(e) => sett('unparkSvar', e.target.value)} />
            </Felt>
          </div>
        ) : null}
        {!ark.kortAapent && (ark.valgt === 'godkjenn' || ark.valgt === 'avslaa') && venterJustering ? (
          <div className="hb-ark" role="group" aria-label={ark.valgt === 'godkjenn' ? 'Godkjenn justeringen' : 'Avslå justeringen'}>
            <p className="hb-dempet">{`Versjon ${venterJustering.revision}: ${justeringTekst}. Grunn: ${venterJustering.reason ?? '—'}`}</p>
            {ark.valgt === 'avslaa' ? <p className="hb-dempet">{`Avslår du, gjelder versjon ${foerJustering?.revision ?? '—'}, og ${fornavn(venterJustering.by)} ser svaret i loggen.`}</p> : null}
            {ark.valgt === 'godkjenn' ? (
              <SvarFelt
                id="hb-godkjenn-svar"
                mottaker={innsender}
                kilde={forslag !== undefined ? `forslaget fra ${fornavn(venterJustering.by)}` : undefined}
                verdi={ark.godkjennSvar}
                onEndre={(v) => sett('godkjennSvar', v)}
                grunn={forslagUendret(ark.godkjennSvar) || feilKode === 'template_unmodified' ? FORSLAG_GRUNN : null}
              />
            ) : (
              <SvarFelt
                id="hb-avslaa-svar"
                mottaker={innsender}
                kilde={forslag !== undefined ? `forslaget fra ${fornavn(venterJustering.by)}` : undefined}
                verdi={ark.avslaaSvar}
                onEndre={(v) => sett('avslaaSvar', v)}
                grunn={forslagUendret(ark.avslaaSvar) || feilKode === 'template_unmodified' ? FORSLAG_GRUNN : null}
              />
            )}
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'losne' ? (
          <div className="hb-ark" role="group" aria-label="Løsne låsen">
            {laaser.length > 1 ? (
              <>
                <span id="hb-losne-h" className="h-etikett">
                  Hvilken lås?
                </span>
                <Radiogruppe etikettId="hb-losne-h" valg={laaser.map((l) => [l.kind, l.navn] as [OverrideKind, string])} aktiv={ark.losneKind} onVelg={(k) => sett('losneKind', k)} testid="hb-losne-laaser" />
              </>
            ) : null}
            {ark.losneKind ? <p className="hb-dempet">{`Låsen: ${laasForklaring(h, ark.losneKind)}`}</p> : null}
            <Felt id="hb-losne-grunn" etikett="Hvorfor løsner du den?" teller={`${ark.losneGrunn.length} / ${SVAR_MAKS}`} grunn={ark.losneGrunn.trim().length > 0 && ark.losneGrunn.trim().length < 20 ? 'Minst 20 tegn.' : null}>
              <textarea id="hb-losne-grunn" className="h-input h-tekstfelt" rows={2} maxLength={SVAR_MAKS} value={ark.losneGrunn} onChange={(e) => sett('losneGrunn', e.target.value)} />
            </Felt>
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'giVidere' ? (
          <div className="hb-ark" role="group" aria-label="Gi videre">
            <Felt id="hb-gv-eier" etikett="Eier" grunn={gvSamme || feilKode === 'owner_is_decider' ? 'Eieren og beslutningstakeren må være to personer.' : null}>
              <select id="hb-gv-eier" className="h-input" value={ark.gvEier} onChange={(e) => sett('gvEier', e.target.value)}>
                {kandidater.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.id === megId ? megSelv(k) : navnAv(k)}
                  </option>
                ))}
                {h.owner && !kandidater.some((k) => k.id === h.owner!.id) ? <option value={h.owner.id}>{`${navnAv(h.owner)} (deaktivert)`}</option> : null}
              </select>
            </Felt>
            <Felt id="hb-gv-besl" etikett="Beslutningstaker">
              <select id="hb-gv-besl" className="h-input" value={ark.gvBesl} onChange={(e) => sett('gvBesl', e.target.value)}>
                {beslutterValg.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.id === megId ? megSelv(k) : navnAv(k)}
                  </option>
                ))}
              </select>
            </Felt>
            <p className="hb-dempet">En ny eier tar eierskapet selv. Ingen begrunnelse — det er en tildeling, ikke en avgjørelse.</p>
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'pause' ? (
          <div className="hb-ark" role="group" aria-label="Sett på pause">
            <Felt id="hb-pause-dato" etikett="Avgjøres" grunn={pauseUtenfor || feilKode === 'revisit_date_out_of_range' ? `Velg en dato fra i morgen til ${datoMedAar(pauseGrense, iDag)}.` : null}>
              <input id="hb-pause-dato" type="date" className="h-input h-input-dato" value={ark.pauseDato} min={iMorgen} max={pauseGrense} aria-invalid={pauseUtenfor || undefined} onChange={(e) => sett('pauseDato', e.target.value)} />
            </Felt>
            <p className="hb-dempet">Senest {datoMedAar(pauseGrense, iDag)}. Den kommer ikke tilbake av seg selv — på datoen står den som «Avgjør nå».</p>
            <Felt id="hb-pause-svar" etikett="Hvorfor pause?" teller={`${ark.pauseSvar.length} / ${SVAR_MAKS}`}>
              <textarea id="hb-pause-svar" className="h-input h-tekstfelt" rows={2} maxLength={SVAR_MAKS} value={ark.pauseSvar} onChange={(e) => sett('pauseSvar', e.target.value)} />
            </Felt>
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'stopp' ? (
          <div className="hb-ark" role="group" aria-labelledby="hb-stopp-h">
            <span id="hb-stopp-h" className="h-etikett">
              Hvorfor stoppes den?
            </span>
            {stoppKoder.length > 0 ? (
              <Radiogruppe
                etikettId="hb-stopp-h"
                valg={stoppKoder.map((k) => [k.code, k.label] as [StopReason, string])}
                aktiv={ark.stoppKode}
                onVelg={(k) => {
                  const start = stoppStart(k);
                  setArk((a) => ({ ...a, stoppKode: k, stoppSvar: start.tekst, maler: { ...a.maler, stopp: start.mal?.id } }));
                }}
                testid="hb-stopp-koder"
              />
            ) : (
              <span className="h-grunn">
                <Ikon navn="lock-closed-outline" className="h-grunn-ikon" />
                <span>Dere har ingen stoppkoder ennå. Eieren setter dem opp.</span>
              </span>
            )}
            <Felt id="hb-stopp-laerte" etikett="Hva vi rakk å lære" teller={`${ark.stoppLaerte.length} / 1000`}>
              <textarea id="hb-stopp-laerte" className="h-input h-tekstfelt" rows={2} maxLength={1000} value={ark.stoppLaerte} onChange={(e) => sett('stoppLaerte', e.target.value)} />
            </Felt>
            <Felt id="hb-stopp-blokk" etikett="Åpne blokkerende funn" grunn={feilKode === 'open_blocking_required' ? '«Ingen» krever en setning om hvorfor.' : null}>
              <textarea id="hb-stopp-blokk" className="h-input h-tekstfelt" rows={2} maxLength={1000} value={ark.stoppBlokk} onChange={(e) => sett('stoppBlokk', e.target.value)} />
            </Felt>
            <Felt id="hb-stopp-data" etikett="Hva dataene ikke tåler" grunn={feilKode === 'data_limitations_required' ? '«Ingen» krever en setning om hvorfor.' : null}>
              <textarea id="hb-stopp-data" className="h-input h-tekstfelt" rows={2} maxLength={1000} value={ark.stoppData} onChange={(e) => sett('stoppData', e.target.value)} />
            </Felt>
            {ark.stoppKode ? (
              <SvarFelt
                id="hb-stopp-svar"
                mottaker={innsender}
                malNavn={stoppKoder.find((k) => k.code === ark.stoppKode)?.label}
                verdi={ark.stoppSvar}
                onEndre={(v) => sett('stoppSvar', v)}
                grunn={stoppMalUendret || feilKode === 'template_unmodified' ? 'Endre malen før du sender.' : null}
              />
            ) : null}
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'bekreft' && h.conclusion?.outcome ? (
          <div className="hb-ark" role="group" aria-label="Bekreft konklusjonen">
            <p className="hb-dempet">{`${stor(innsenderFor)} får: ${loefteVed(config, h, { status: 'konkludert', outcome: h.conclusion.outcome }) ?? ''}`}</p>
            <SvarFelt id="hb-bekreft-svar" mottaker={innsender} verdi={ark.bekreftSvar} onEndre={(v) => sett('bekreftSvar', v)} />
          </div>
        ) : null}
        {!ark.kortAapent && ark.valgt === 'tilbake' ? (
          <div className="hb-ark" role="group" aria-label="Send tilbake med merknad">
            <Felt id="hb-tilbake" etikett={`Merknad til ${navnAv(h.owner)}`} teller={`${ark.tilbakeMerknad.length} / ${SVAR_MAKS}`}>
              <textarea id="hb-tilbake" className="h-input h-tekstfelt" rows={2} maxLength={SVAR_MAKS} value={ark.tilbakeMerknad} onChange={(e) => sett('tilbakeMerknad', e.target.value)} />
            </Felt>
          </div>
        ) : null}
      </div>
    ) : null;

  // --- Hvem som har hypotesen (P6–P8b): eieren, beslutningstakeren og vurdereren -----------
  const eierTekst = h.owner ? (h.owner.id === megId ? 'Du' : `${stor(navnAv(h.owner))}${deaktivert ? ' (deaktivert)' : ''}`) : null;
  const roller_linje =
    !eierTekst || !h.decider
      ? null
      : h.status === 'konkludert'
        ? `${h.owner!.id === megId ? 'Du' : stor(navnAv(h.owner))} eide · ${hvem(h.conclusion?.confirmedBy ?? h.decider, megId, 'du')} bekreftet`
        : h.status === 'valgt' || h.status === 'testes' || h.status === 'pa_pause'
          ? `${eierTekst} eier · ${roller.avgjoer ? 'du avgjør' : `${navnAv(h.decider)} avgjør`}${h.status === 'valgt' && h.reviewer ? ` · ${roller.vurderer ? 'du' : navnAv(h.reviewer)} vurderte` : ''}`
          : null;

  // --- Resten av flaten ------------------------------------------------------------------
  const svar = h.responses.filter((r) => r.kind !== 'trenger_mer').slice().reverse();
  const sporsmaal = h.responses.filter((r) => r.kind === 'trenger_mer');
  const sisteSporsmaal = sporsmaal[sporsmaal.length - 1];
  // Under 900 står detaljen alene: tittelen er sidens h1, og underoverskriftene ett nivå opp.
  const TittelTag = smal ? 'h1' : 'h2';
  const UnderTag = smal ? 'h2' : 'h3';
  // Versjonen som gjelder (approvedRevision) i sporet etter valgt, som konseptsiden («· versjon 2»).
  const konklusjonsside = konklusjonVenter(h);
  const versjon = h.status === 'valgt' || h.status === 'testes' || h.status === 'pa_pause' ? (h.approvedRevision ?? h.currentRevision) : undefined;
  // H3.1: grunnlaget mellom setningen og funnene; etter konklusjon og stopp står notisen først.
  const grunnlag =
    h.status === 'utkast' ? null : (
      <GrunnlagSeksjon
        h={h}
        megId={megId}
        tittelTag={UnderTag}
        kanLenke={kanLenke}
        fjernes={fjernes}
        onFjern={(sak) => hold({ handling: 'removeEvidence', submissionId: sak.id }, `${saksnr(sak.number)} er tatt ut av grunnlaget.`)}
        onKvittering={p.onKvittering}
        onFeil={p.onFeil}
        velgerAapen={velgerAapen}
        setVelgerAapen={setVelgerAapen}
      />
    );
  // H3.4 (skjerm 2): «Betaprogram» etter grunnlaget, før funnene. Hjelpesetningen står til rapporten er fryst.
  const betaprogram = (
    <BetaprogramSeksjon items={prgItems} megId={megId} tittelTag={UnderTag} hjelp={roller.eier && (h.status === 'valgt' || h.status === 'testes') && !h.conclusion && !prgItems.some((x) => x.report)} />
  );
  const lenker =
    h.status === 'utkast' ? null : (
      <LenkerSeksjon
        h={h}
        kanEndre={lenkerKan}
        tittelTag={UnderTag}
        fjernes={fjernesLenke}
        onFjern={(l) => hold({ handling: 'removeLink', linkId: l.id, label: l.label }, `Lenken «${l.label}» er fjernet.`)}
        onKvittering={p.onKvittering}
      />
    );
  const notisOgGrunnlag = (
    <>
      <NotisSeksjon h={h} megId={megId} tenantName={tenantName} tittelTag={UnderTag} tekst={ark.notis} setTekst={(t) => sett('notis', t)} kanLenke={kanLenke} onLeggTil={() => setVelgerAapen(true)} />
      {grunnlag}
    </>
  );

  return (
    <section className="hb-detalj" aria-labelledby="hb-tittel" data-testid="hb-detalj">
      {smal ? (
        <header className="h-stripe" data-testid="hb-stripe">
          <Link to={p.fra.til} className="panel-stripe-tilbake" aria-label={`Tilbake til ${p.fra.navn}`}>
            <Ikon navn="chevron-back-outline" />
          </Link>
          <span className="h-stripe-tittel">{p.posisjon ?? nr}</span>
          {enSide ? (
            <a href={enSide} target="_blank" rel="noopener" className="hb-stripe-knapp" aria-label="Én side" aria-keyshortcuts="e" data-testid="hb-en-side">
              <Ikon navn="document-text-outline" />
            </a>
          ) : null}
          {rom.leserPluss ? (
            <button type="button" className="hb-stripe-knapp" aria-label="Internt notat" aria-keyshortcuts="n" aria-pressed={ark.notatAapent} onClick={() => (ark.notatAapent ? sett('notatAapent', false) : aapneNotat())}>
              <Ikon navn="create-outline" />
            </button>
          ) : null}
        </header>
      ) : null}
      <div className="hb-detalj-rull">
        <div className="hb-detalj-innhold">
          {banner}
          {usendt}
          {h.watchChangedAt ? (
            // H3.3: en fulgt hypotese står i «Til deg» til personen sier «OK» — aldri fordi den ble lest (Kh9).
            <div className="hb-kort hi-foelg" data-testid="hb-foelg-kort">
              <p className="hi-varsel">
                <Ikon navn="notifications-outline" />
                <span>
                  Du følger denne. {stor(statusNavn(config, h) || h.status)} siden {kortDatoAv(h.statusChangedAt)}.
                </span>
              </p>
              <div>
                <button type="button" className="panel-knapp-sekundaer" aria-busy={kvitter.isPending || undefined} onClick={() => kvitter.mutate(h.id)}>
                  OK, ta den ut av Til deg
                </button>
              </div>
            </div>
          ) : null}
          {p.alene && !smal ? (
            <Link to={p.fra.til} className="panel-knapp-lenke hb-tilbake" data-testid="hb-tilbake">
              <Ikon navn="chevron-back-outline" />
              {p.fra.navn}
            </Link>
          ) : null}
          <div className="hb-spor-topp">
            <span>
              {/* «Til deg /» eller «Innboks /» — siden detaljen ble åpnet fra, med lista ved siden av. Fra
                  registeret står detaljen alene, og lenken over sier «Registeret» (konseptets skjerm 6). */}
              {p.alene ? '' : `${p.fra.navn} / `}Hypotese {nr} · fra {hvem(h.submittedBy, megId, 'deg')} {h.submittedAt ? kortDatoAv(h.submittedAt) : ''}
              {/* H3.3 (W13): medinnsenderne fra sammenslåinger, med hypotesen de kom fra. */}
              {(h.coSubmitters ?? []).map((c) => ` · og ${hvem(c.admin, megId, 'deg')} (${nummerTekst(c.fromHypothesis.number)})`).join('')}
              {versjon !== undefined ? ` · versjon ${versjon}` : ''}
            </span>
            {/* Rollene på hypotesen til høyre på sporets linje, som konseptsiden (skjerm 6–8b). */}
            {roller_linje || !smal ? (
              <span className="hb-spor-topp-hoyre">
                {roller_linje ? (
                  <span className="hb-roller" data-testid="hb-roller">
                    {roller_linje}
                  </span>
                ) : null}
                {!smal && enSide ? (
                  <a href={enSide} target="_blank" rel="noopener" className="panel-knapp-lenke hb-notat-knapp" aria-keyshortcuts="e" data-testid="hb-en-side">
                    <Ikon navn="document-text-outline" />
                    Én side <kbd>E</kbd>
                  </a>
                ) : null}
                {!smal && rom.leserPluss ? (
                  <button type="button" className="panel-knapp-lenke hb-notat-knapp" aria-pressed={ark.notatAapent} aria-keyshortcuts="n" onClick={() => (ark.notatAapent ? sett('notatAapent', false) : aapneNotat())} data-testid="hb-notat-knapp">
                    <Ikon navn="create-outline" />
                    Notat <kbd>N</kbd>
                  </button>
                ) : null}
                {!smal ? <kbd aria-hidden="true">J</kbd> : null}
                {!smal ? <kbd aria-hidden="true">K</kbd> : null}
              </span>
            ) : null}
          </div>
          {/* H2.8 (DD-80): lagt inn fra en tidligere kanal — sagt rett ut, for alle, i én linje øverst. */}
          {h.importedBy ? (
            <small className="hb-liten hb-lagt-inn" data-testid="hb-lagt-inn">
              {`Lagt inn av ${hvem(h.importedBy, megId, 'deg')}${h.importedAt ? ` ${kortDatoMedAarAv(h.importedAt)}` : ''}${h.importSource ? ` fra ${h.importSource}` : ''} · opprinnelig sendt${h.submittedAt ? ` ${kortDatoMedAarAv(h.submittedAt)}` : ''} av ${hvem(h.submittedBy, megId, 'deg')}`}
            </small>
          ) : null}
          <TittelTag id="hb-tittel" className="hb-tittel">
            {h.title}
          </TittelTag>
          {konklusjonsside ? (
            // Konseptsidens skjerm 8: pillen og «konklusjonen er skrevet av … · venter på … · frist …»
            // under tittelen, ingen sporingslinje; tallene, utfallet som ord, radene, og avgjørelsen under.
            <>
              <div className="hb-naa-pille" data-testid="hb-konklusjon-topp">
                <HypotesePille noekkel="testes" navn={noekkelNavn} />
                <span className="hb-dempet">
                  {`konklusjonen er skrevet av ${hvem(h.conclusion!.writtenBy, megId, 'deg')} ${kortDatoAv(h.conclusion!.writtenAt)} · venter på ${hvem(h.decider, megId, 'deg')}${h.conclusionDueAt ? ` · frist ${kortDatoAv(h.conclusionDueAt)}` : ''}`}
                </span>
              </div>
              <Konklusjonsblokk h={h} config={config} megId={megId} avgjoer={roller.avgjoer} />
              {neste ? (
                <div role="group" aria-label="Status og neste steg" className="hb-statusblokk">
                  {neste}
                </div>
              ) : null}
              {lenker}
              {grunnlag}
              {betaprogram}
            </>
          ) : h.status === 'konkludert' ? (
            // Skjerm 8b: pillen og «bekreftet … · fryst», sporingslinjen, tallene og radene. Ingen «Nå», intet løfte.
            <>
              <div className="hb-naa-pille">
                <HypotesePille noekkel={h.outcome ?? 'konkludert'} navn={noekkelNavn} />
                {h.conclusion?.frozenAt ? (
                  <span className="hb-dempet" data-testid="hb-fryst">
                    bekreftet {kortDatoAv(h.conclusion.confirmedAt ?? h.conclusion.frozenAt)} · fryst
                  </span>
                ) : null}
              </div>
              <Sporingslinje ledd={spor} etikett="Hvor hypotesen står" />
              <Konklusjonsblokk h={h} config={config} megId={megId} avgjoer={false} />
              {lenker}
              {notisOgGrunnlag}
              {betaprogram}
            </>
          ) : (
            // DD-57 og skjerm 6: tekstkolonnen med lesebredde, og handlingsfeltet (sjekklista før start) til
            // høyre når det er plass — ellers under. Ingen ny breakpoint.
            <div className="hb-kropp">
              <div className="hb-kropp-tekst">
                <div role="group" aria-label="Status og neste steg" className="hb-statusblokk">
                  <Sporingslinje ledd={spor} etikett="Hvor hypotesen står" />
                  <div className="hb-naa-neste">
                    {naa}
                    {neste}
                  </div>
                </div>
                {h.sentence ? <p className="hb-setning">{h.sentence}</p> : null}
                {lenker}
                {h.status === 'stoppet' ? null : grunnlag}
                {h.conclusion?.frozenAt ? <Konklusjonsblokk h={h} config={config} megId={megId} avgjoer={false} /> : null}
                {h.status === 'stoppet' ? notisOgGrunnlag : null}
                {betaprogram}
                <Fakta h={h} megId={megId} />
                <ByggerPaa h={h} config={config} tittelTag={UnderTag} />
                {kanSlaaSammen ? <LignerPaaKandidater kandidater={lignende.data ?? []} tittelTag={UnderTag} onSlaaSammen={setSlaar} /> : null}
                <Funnliste h={h} megId={megId} />
              </div>
              {h.status === 'valgt' ? (
                <div className="hb-kropp-felt" data-testid="hb-handlingsfelt">
                  <Sjekkliste h={h} config={config} megId={megId} />
                </div>
              ) : null}
            </div>
          )}
          {h.status === 'mottatt' && roller.behandler && !roller.innsender && ark.valgt !== 'avvis' ? (
            <div role="group" aria-labelledby="hb-vurdering-h" className="hb-vurdering">
              <UnderTag id="hb-vurdering-h" className="panel-h3">
                Din vurdering
              </UnderTag>
              <VurderingRad id="hb-kv-bomme" spm="Kan den bomme?" valg={[['ja', 'Ja'], ['nei', 'Nei']]} aktiv={ark.kanBomme} onVelg={(v) => sett('kanBomme', v)} />
              <VurderingRad id="hb-kv-kilde" spm="Kilden" valg={[['ja', 'Ja, finnes'], ['trenger_avklaring', 'Trenger avklaring'], ['maa_bygges', 'Må bygges']]} aktiv={ark.kilde} onVelg={(v) => sett('kilde', v)} />
              <VurderingRad id="hb-kv-base" spm="Baseline" valg={[['ja', 'Ja, målt'], ['maa_maales', 'Må måles']]} aktiv={ark.baseline} onVelg={(v) => sett('baseline', v)} />
              <Felt id="hb-kv-merknad" etikett="Merknad · valgfri">
                <textarea id="hb-kv-merknad" className="h-input h-tekstfelt" rows={2} maxLength={280} value={ark.merknad} onChange={(e) => sett('merknad', e.target.value)} />
              </Felt>
              {ark.kilde === 'maa_bygges' ? <p className="hb-dempet">Testen kan ikke starte før målingen er bygget.</p> : null}
            </div>
          ) : null}
          {sisteSporsmaal ? (
            <div className="hb-kort" data-testid="hb-sporsmaal">
              <div className="hb-kort-hode">
                <b>
                  {hvem(sisteSporsmaal.by, megId, 'Du')} spurte {innsenderFor} · {h.awaitingSubmitter ? 'venter på svar' : 'besvart'}
                </b>
                <span className="hb-liten">
                  {kortDatoAv(sisteSporsmaal.at)} · {h.needsMoreCount} av {MAKS_TRENGER_MER}
                </span>
              </div>
              <p className="hb-tekst">{sisteSporsmaal.text}</p>
              {h.awaitingSubmitter ? <p className="hb-dempet">{innsenderFor} svarer ved å endre hypotesen. Fristen løper imens.</p> : null}
            </div>
          ) : null}
          {svar.map((s) => (
            <div key={s.id} className="hb-svar">
              <div className="hb-svar-hode">
                <b>Svar fra {hvem(s.by, megId, 'deg')}</b>
                <span>{kortDatoAv(s.at)}</span>
              </div>
              <p className="hb-tekst">{s.text}</p>
            </div>
          ))}
          {h.status !== 'utkast' ? <Interesse h={h} megId={megId} tittelTag={UnderTag} knapperIFoten={interesseIFoten} onGiData={() => setDataAapen(true)} /> : null}
          {rom.leserPluss && ark.notatAapent ? (
            <Felt id="hb-notat" etikett="Internt notat · forlater aldri panelet">
              <textarea id="hb-notat" ref={notatRef} className="h-input h-tekstfelt" rows={3} maxLength={1000} value={ark.notat} onChange={(e) => sett('notat', e.target.value)} />
            </Felt>
          ) : null}
          {rom.leserPluss
            ? (notater.data ?? []).map((n) => (
                <div key={n.id} className="hb-notat" data-testid="hb-notat">
                  <div className="hb-svar-hode">
                    <span>
                      <Ikon navn="create-outline" /> <b>Internt notat</b> · {hvem(n.by, megId, 'du')} · forlater aldri panelet
                    </span>
                    <span>{kortDatoAv(n.at)}</span>
                  </div>
                  <p className="hb-tekst">{n.text}</p>
                </div>
              ))
            : null}
          <Logg h={h} config={config} megId={megId} programmer={prgItems} moeter={moeter.data} moeteLenke={rom.leserPluss} />
        </div>
      </div>
      {utenFot ? null : (
        <Fot
          primarFoerst={fot.primarFoerst}
          venstre={
          fot.venstreAlltid ?? (fot.grunn ? undefined : (
            <span className="hb-fot-tekst">
              {fot.setning ? <span>{fot.setning}</span> : null}
              {fot.oppsummering ? <span className="hb-liten">{fot.oppsummering}</span> : null}
            </span>
          ))
        }
        grunn={fot.grunn}
          primar={{ label: fot.label, onClick: fot.onClick, opptatt: venter || direkte.isPending || leggTilNotat.isPending, tast: fot.tast ?? 'ctrl-enter', testid: 'hb-primar', ikon: fot.ikon }}
        />
      )}
      {slaar ? (
        <SlaaSammenDialog
          h={h}
          kandidat={slaar}
          config={config}
          onLukk={() => setSlaar(null)}
          onHold={(v) => {
            setSlaar(null);
            p.onHold(v);
          }}
        />
      ) : null}
      {/* P8: detaljen skrives aldri ut — den kan ha notater. Utskriften gir én linje i stedet, som står
          utenfor panelet i <body> og vises bare på papir (hypoteser.css, @media print). */}
      {createPortal(
        <p className="hb-utskrift-linje" data-innspel-theme="light" data-testid="hb-utskrift-linje">
          {enSide ? `Bruk «Én side» på ${nr} — den tar ikke med notater.` : `Detaljen skrives ikke ut, fordi den kan ha notater. «Én side» finnes når ${nr} er konkludert eller stoppet.`}
        </p>,
        document.body,
      )}
      {dataAapen ? <DataDialog h={h} megId={megId} megNavn={p.megNavn} min={config?.minFieldLength ?? 10} onLukk={() => setDataAapen(false)} onLagret={() => setDataAapen(false)} /> : null}
    </section>
  );
}

/**
 * Et neste steg. En overgang er fylt i flaten til statusen den gir (DD-56); et steg som ikke er en
 * overgang, står i den nøytrale flaten med sitt eget ikon og uten S-tast. Ingen er rød — «Stopp
 * testen …» har Stoppets nøytrale flate (DD-53). Et steg som åpner en egen side («Juster før
 * start …», `til`), er en lenke med omriss, som konseptsidens skjerm 6.
 */
function HandlingKnapp({ x, valgt, smal, til, onVelg }: { x: Handling; valgt: boolean; smal: boolean; til?: string; onVelg: () => void }) {
  const o = x.flate ? hypoteseOppforing(x.flate) : { fg: 'textSecondary', bg: 'neutralBg', ikon: x.ikon ?? 'ellipsis-horizontal-outline' };
  const style = { '--hb-fg': tokenVar(o.fg), '--hb-bg': tokenVar(o.bg) } as CSSProperties;
  if (til) {
    return (
      <Link to={til} className="hb-handling hb-handling-omriss" data-testid={`hb-handling-${x.k}`}>
        <Ikon navn={o.ikon} />
        {x.navn}
      </Link>
    );
  }
  return (
    <button type="button" className="hb-handling" aria-pressed={valgt} aria-keyshortcuts={x.tast ? `s ${x.tast.toLowerCase()}` : undefined} onClick={onVelg} style={style} data-testid={`hb-handling-${x.k}`}>
      <Ikon navn={o.ikon} />
      {x.navn}
      {smal || !x.tast ? null : <kbd>S {x.tast}</kbd>}
    </button>
  );
}

/** Låsen i ord, med det innsenderen sa om kilden eller baseline. */
function laasForklaring(h: Hypothesis, kind: OverrideKind): string {
  if (kind === 'uavklart_kilde') return `kilden er uavklart${h.sourceClaim ? ` — ${KILDE[h.sourceClaim] ?? ''}` : ''}.`;
  return `baseline er ikke målt${h.baselineOn ? ` — ${h.baselineState === 'maalt' ? 'vurdereren sa at den må måles' : `ikke målt per ${kortDato(h.baselineOn)}`}` : ''}.`;
}

export function Felt({ id, etikett, teller, grunn, merke, hint, children }: { id: string; etikett: string; teller?: string; grunn?: string | null; merke?: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="h-felt">
      <div className="h-felt-hode">
        <label htmlFor={id} className="hb-felt-etikett">
          {etikett}
        </label>
        {/* H3.4: «fra rapporten» ved etiketten, til hun endrer feltet (konseptets skjerm 11). */}
        {merke}
        {teller ? <span className="h-teller">{teller}</span> : null}
      </div>
      {children}
      {hint}
      {grunn ? (
        <span className="h-felt-grunn" data-testid={`${id}-grunn`}>
          <Ikon navn="warning-outline" className="h-varsel-ikon-warning" />
          <span>{grunn}</span>
        </span>
      ) : null}
    </div>
  );
}

/** Svaret til innsenderen: malen i feltet, og den stiplede plassen der hvorfor-et mangler (DD-09). */
function SvarFelt({ id, mottaker, malNavn, kilde, verdi, onEndre, grunn }: { id: string; mottaker: string; malNavn?: string; kilde?: string; verdi: string; onEndre: (v: string) => void; grunn?: string | null }) {
  const plass = MAL_PLASSER.find((x) => verdi.includes(x));
  const i = plass ? verdi.indexOf(plass) : -1;
  return (
    <Felt id={id} etikett={`Svar til ${mottaker}${malNavn ? ` · malen for «${malNavn}»` : kilde ? ` · ${kilde}` : ''}`} teller={`${verdi.length} / ${SVAR_MAKS}`} grunn={grunn}>
      <textarea id={id} className="h-input h-tekstfelt" rows={3} maxLength={SVAR_MAKS} value={verdi} onChange={(e) => onEndre(e.target.value)} />
      {plass && i >= 0 ? (
        <p className="hb-dempet hb-mal" data-testid={`${id}-mal`}>
          {verdi.slice(0, i)}
          <span className="hb-mal-plass">{plass}</span>
          {verdi.slice(i + plass.length)}
        </p>
      ) : null}
    </Felt>
  );
}

function VurderingRad<T extends string>({ id, spm, valg, aktiv, onVelg }: { id: string; spm: string; valg: Array<[T, string]>; aktiv: T | undefined; onVelg: (v: T) => void }) {
  return (
    <div className="hb-vurdering-rad">
      <span id={id} className="hb-fet">
        {spm}
      </span>
      <Radiogruppe etikettId={id} valg={valg} aktiv={aktiv} onVelg={onVelg} testid={id} />
    </div>
  );
}

const FREKVENS: Record<string, string> = { daglig: 'daglig', ukentlig: 'ukentlig', maanedlig: 'månedlig', kvartalsvis: 'kvartalsvis', sjeldnere: 'sjeldnere enn kvartalsvis' };
const KILDE: Record<string, string> = { avklart: 'innsenderen sier den er avklart', uklar: 'innsenderen sier definisjonen er uklar', maa_bygges: 'innsenderen sier den må bygges' };

/** Faktaene fra skjemaet: kilden og oppdateringen synlige der noen skal vurdere (DD-67: body). */
function Fakta({ h, megId }: { h: Hypothesis; megId: string | undefined }) {
  const rader: Array<[string, string, string?]> = [];
  // H3.3 (W13): ledd hentet over ved en sammenslåing står merket «fra H-149».
  const hentet = new Map<string, string>();
  for (const r of h.revisions) {
    if (r.kind !== 'sammenslaaing' || !r.fromHypothesis) continue;
    for (const c of r.carriedOver ?? []) hentet.set(c, `fra ${nummerTekst(r.fromHypothesis.number)}`);
  }
  if (h.baselineState) {
    const verdi = h.baselineState === 'maalt' && h.baselineValue != null ? `${tall(h.baselineValue)} ${h.unit ?? ''}`.trim() : 'Ikke målt';
    rader.push(['I dag', `${verdi}${h.baselineOn ? ` · ${h.baselineState === 'maalt' ? 'målt' : 'per'} ${kortDato(h.baselineOn)}` : ''}${h.updateFrequency ? ` · oppdateres ${FREKVENS[h.updateFrequency] ?? ''}` : ''}`]);
  }
  if (h.metricSource) {
    // W11: kilden fra kildelisten, og hvem som bekreftet definisjonen.
    const status =
      h.sourceStatus === 'verifisert'
        ? h.sourceVerifiedBy
          ? ` · definisjonen avklart av ${hvem(h.sourceVerifiedBy, megId, 'deg')}${h.sourceVerifiedAt ? ` ${kortDatoAv(h.sourceVerifiedAt)}` : ''}`
          : ' · definisjonen er avklart'
        : h.sourceStatus === 'maa_bygges'
          ? ' · må bygges'
          : ' · uklar definisjon';
    rader.push(['Kilde', `${h.metricSource.name} · fra kildelisten${status}`]);
  } else if (h.sourceText) rader.push(['Kilde', `${h.sourceText}${h.sourceClaim ? ` · ${KILDE[h.sourceClaim] ?? ''}` : ''}`]);
  if (h.notDoing) rader.push(['Det vi ikke gjør', h.notDoing]);
  if (h.whatWeLose) rader.push(['Hva vi mister', h.whatWeLose, hentet.get('what_we_lose')]);
  if (h.background) rader.push(['Hvorfor', h.background, hentet.get('background')]);
  if (rader.length === 0) return null;
  return (
    <dl className="hb-fakta">
      {rader.map(([navn, verdi, fra]) => (
        <div key={navn} className="hb-fakta-rad">
          <dt>{navn}</dt>
          <dd>
            {verdi}
            {fra ? <span className="hs-fra">{fra}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function overgangOrd(til: string): string {
  return (
    {
      trukket: 'trakk hypotesen',
      vurderes: 'tok den til vurdering',
      avvist: 'avviste den',
      parkert: 'parkerte den',
      valgt: 'valgte den',
      testes: 'startet testen',
      pa_pause: 'satte den på pause',
      stoppet: 'stoppet testen',
      konkludert: 'bekreftet konklusjonen',
    } as Record<string, string>
  )[til] ?? 'endret den';
}

const klokke = (t: string) => new Date(t).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Oslo' });

/**
 * Loggen: tidslinjen kontrakten gir, nyeste først. Ingen «så den»-rader (Kh9). Radene skilles på
 * `kind` («Hypotesekontrakten for H2.3 andre runde», 2026-10-08) — aldri på tidspunktet eller på
 * `fromStatus`/`toStatus`, som er like for alle radene der statusen står.
 */
export function Logg({ h, config, megId, programmer = [], moeter, moeteLenke = false }: { h: Hypothesis; config: HypothesisConfig | undefined; megId: string | undefined; programmer?: HypothesisProgramRef[]; /** H3.2 (W11b): møtene hypotesen har vært på — bare for leser og opp. */ moeter?: HypothesisMeetingEntry[]; /** Lenken «Åpne beslutningsloggen» — for den som kan lese møtet. */ moeteLenke?: boolean }) {
  type Rad = { hva: string; detalj?: string | null; at: string; noekkel: string; lenke?: { til: string; tekst: string }; /** H2.8: «registrert i ettertid» etter datoen. */ ettertid?: boolean };
  const rader: Rad[] = [];
  // Justeringene kommer én og én: en ny kan ikke foreslås før den forrige er avgjort. Den n-te
  // foreslåtte og den n-te avgjorte raden hører derfor til den n-te justeringen.
  const justeringer = h.revisions.filter((r) => r.kind === 'justering');
  const avgjorte = h.transitions.filter((t) => t.kind === 'justering_godkjent' || t.kind === 'justering_avslatt').length;
  let foreslaatt = 0;
  let avgjort = 0;
  for (const t of h.transitions) {
    const hvemGjorde = hvem(t.by, megId, 'Du');
    const rad = (hva: string, detalj: string | null = null) => rader.push({ hva, detalj, at: t.at, noekkel: t.id, ...(t.imported ? { ettertid: true } : {}) });
    switch (t.kind) {
      case 'justering_foreslatt': {
        const rev = justeringer[foreslaatt];
        foreslaatt += 1;
        if (!rev) {
          rad(`${hvemGjorde} foreslo en justering`);
          break;
        }
        const foer = revisjonenFoer(h, rev);
        const endret = foer ? endringTekst(endredeLedd(foer.form, rev.form)) : '';
        const venter = !rev.approvedBy && !rev.declinedBy;
        // Eldre data har ingen rad for avgjørelsen (før 2026-10-08): da står utfallet her.
        const utenRad = !venter && foreslaatt > avgjorte;
        const utfall = venter
          ? `Venter på ${hvem(h.decider, megId, 'deg')}. Gjeldende form er versjon ${foer?.revision ?? '—'}.`
          : utenRad
            ? rev.approvedBy
              ? `Godkjent av ${hvem(rev.approvedBy, megId, 'deg')}.`
              : `Avslått av ${hvem(rev.declinedBy, megId, 'deg')}.`
            : '';
        rad(`${hvemGjorde} foreslo en justering (versjon ${rev.revision})${endret ? `: ${endret}` : ''}`, `${rev.reason ?? ''} ${utfall}`.trim() || null);
        break;
      }
      case 'justering_godkjent':
      case 'justering_avslatt': {
        const rev = justeringer[avgjort];
        avgjort += 1;
        const nr = rev ? ` (versjon ${rev.revision})` : '';
        if (t.kind === 'justering_godkjent') rad(`${hvemGjorde} godkjente justeringen${nr}`, rev ? `Versjon ${rev.revision} gjelder.` : null);
        else {
          const gjelder = rev ? revisjonenFoer(h, rev) : undefined;
          rad(`${hvemGjorde} avslo justeringen${nr}`, gjelder ? `Versjon ${gjelder.revision} gjelder fortsatt.` : null);
        }
        break;
      }
      case 'maling_klar':
        rad(`${hvemGjorde} meldte at målingen er klar`);
        break;
      case 'kilde_verifisert':
        rad(`${hvemGjorde} verifiserte kilden`);
        break;
      case 'overstyring':
        rad(`${hvemGjorde} løsnet låsen «${t.override === 'baseline_mangler' ? 'baseline er ikke målt' : 'kilden er uavklart'}»`, t.overrideReason ? `«${t.overrideReason}»` : null);
        break;
      case 'gitt_videre':
        rad(`${hvemGjorde} ga hypotesen videre`);
        break;
      case 'eierskap_tatt':
        rad(`${hvemGjorde} tok eierskapet`);
        break;
      case 'eierskap_avslatt':
        rad(`${hvemGjorde} sa nei til eierskapet`);
        break;
      case 'sammenslaaing': {
        // H3.3 (W13), på målet: hvem som slo hvilken sammen med denne, og at begge fikk svaret.
        const fra = t.mergedFrom;
        const med = fra ? (h.coSubmitters ?? []).filter((c) => c.fromHypothesis.id === fra.id).map((c) => navnAv(c.admin)) : [];
        rad(`${hvemGjorde} slo ${fra ? nummerTekst(fra.number) : 'en lignende'}${med.length ? ` fra ${med.join(' og ')}` : ''} sammen med denne`, med.length ? `${med.join(' og ')} står som medinnsender. Begge fikk svaret.` : 'Begge fikk svaret.');
        break;
      }
      default: {
        const kode = t.rejectReason
          ? config?.rejectReasonWording.find((w) => w.code === t.rejectReason)?.label
          : t.stopReason
            ? config?.stopReasonWording.find((w) => w.code === t.stopReason)?.label
            : t.toStatus === 'konkludert' && h.outcome
              ? statusNavn(config, { status: 'konkludert', outcome: h.outcome })
              : t.override === 'ett_maalepunkt'
                ? 'Startet likevel med under to målepunkter'
                : undefined;
        const hva =
          t.fromStatus === 'utkast' && t.toStatus === 'mottatt'
            ? `${hvemGjorde} sendte den inn`
            : t.fromStatus === 'parkert' && t.toStatus === 'vurderes'
              ? `${hvemGjorde} tok den tilbake til vurdering`
              : t.fromStatus === 'pa_pause' && t.toStatus !== 'stoppet'
                ? `${hvemGjorde} fortsatte ${t.toStatus === 'testes' ? 'testen' : 'den'}`
                : t.toStatus === 'slaatt_sammen' && t.mergedInto
                  ? `${hvemGjorde} slo den sammen med ${nummerTekst(t.mergedInto.number)}`
                  : `${hvemGjorde} ${overgangOrd(t.toStatus)}`;
        // H3.2 (W11b, W12b, P6): overgangen fra et møte bærer møtets navn og dato for alle — og hvem
        // som registrerte bare når tjeneren gir det (aldri til innsenderen).
        if (t.meeting) {
          const navn = moeteNavn(t.meeting);
          rader.push({
            hva: t.registeredBy ? `${hva} — i ${navn}, registrert av ${hvem(t.registeredBy, megId, 'deg')}` : `${hva} i ${navn}`,
            detalj: kvalitetssjekkTekst(t) ?? kode ?? null,
            at: t.at,
            noekkel: t.id,
            ...(t.imported ? { ettertid: true } : {}),
            ...(moeteLenke ? { lenke: { til: `/h/moter/${t.meeting.id}`, tekst: 'Åpne beslutningsloggen' } } : {}),
          });
          break;
        }
        rad(hva, kvalitetssjekkTekst(t) ?? kode ?? null);
      }
    }
  }
  // Eldre data: eierskapet ble ikke en rad før 2026-10-08. Feltet står da for raden — aldri i tillegg.
  if (h.ownerAcceptedAt && h.owner && !h.transitions.some((t) => t.kind === 'eierskap_tatt')) rader.push({ hva: `${hvem(h.owner, megId, 'Du')} tok eierskapet`, at: h.ownerAcceptedAt, noekkel: 'eierskap' });
  if (h.conclusion?.kind === 'konklusjon') {
    // H3.4: skrev hun med programrapporten som utgangspunkt, sier linjen det (skjerm 15).
    rader.push({ hva: `${hvem(h.conclusion.writtenBy, megId, 'Du')} skrev konklusjonen${h.conclusion.programReportId ? ', med programrapporten som utgangspunkt' : ''}`, at: h.conclusion.writtenAt, noekkel: 'konklusjon' });
    if (h.conclusion.sentBackAt) rader.push({ hva: `${hvem(h.decider, megId, 'Du')} sendte konklusjonen tilbake`, detalj: h.conclusion.sentBackNote ? `«${h.conclusion.sentBackNote}»` : null, at: h.conclusion.sentBackAt, noekkel: 'tilbake' });
  }
  for (const r of h.responses) {
    if (r.kind !== 'trenger_mer') continue;
    const kort = r.text.length > 60 ? `${r.text.slice(0, 60)} …` : r.text;
    rader.push({ hva: `${hvem(r.by, megId, 'Du')} spurte: «${kort}»`, at: r.at, noekkel: r.id });
  }
  for (const d of h.deadlineChanges) {
    rader.push({ hva: `${hvem(d.by, megId, 'Du')} kortet ${d.kind === 'conclusion' ? 'konklusjonsfristen' : 'fristen'} til ${kortDatoAv(d.toAt)}`, at: d.at, noekkel: d.id });
  }
  for (const rev of h.revisions) {
    if (rev.kind !== 'innsender_endret') continue;
    rader.push({ hva: `${hvem(rev.by, megId, 'Du')} endret hypotesen`, detalj: rev.reason ?? null, at: rev.at, noekkel: rev.id });
  }
  // H3.1 (skjerm 12): notisen og «Ingen notis» er avgjørelser, og loggen fører dem — aldri teksten.
  if (h.conclusionNotice) {
    const n = h.conclusionNotice;
    rader.push({ hva: `${hvem(n.by, megId, 'Du')} sendte notisen til ${n.recipientCount} ${n.recipientCount === 1 ? 'bruker' : 'brukere'}`, at: n.sentAt, noekkel: n.id });
  }
  if (h.conclusionNoticeSkipped) {
    const s = h.conclusionNoticeSkipped;
    rader.push({ hva: `${hvem(s.by, megId, 'Du')} valgte ingen notis til brukerne`, at: s.at, noekkel: `ingen-notis-${h.id}` });
  }
  // H3.4: programmet ved de to datoene det betyr noe for hypotesen — laget, og rapporten fryst.
  rader.push(...programLoggRader(programmer, megId));

  // H3.2 (W11b): agendaen og tallene fra møtet — en egen hendelse med den som satte dem, aldri en sum.
  for (const e of moeter ?? []) {
    const navn = moeteNavn(e.meeting);
    rader.push({ hva: `Satt på agendaen til ${navn} av ${hvem(e.addedBy, megId, 'deg')}`, at: e.addedAt, noekkel: `agenda-${e.meeting.id}` });
    if (e.score) {
      const linje = tallTekst(e.score);
      if (linje) rader.push({ hva: `Tall satt i møtet: ${linje}`, detalj: `${stor(hvem(e.score.setBy, megId, 'deg'))} · ingen sum regnes`, at: e.score.setAt, noekkel: `tall-${e.meeting.id}` });
    }
  }
  if (rader.length === 0) return null;
  rader.sort((a, b) => b.at.localeCompare(a.at));
  return (
    <ol className="hb-logg" aria-label="Loggen">
      {rader.map((r) => (
        <li key={r.noekkel} className="hb-logg-rad">
          <span className="hb-fet">{r.hva}</span>
          {/* H2.8 (DD-80): i samme small som møtets navn — synlig for alle, ingenting å skjule. */}
          <span className="hb-liten">{r.ettertid ? `${kortDatoAv(r.at)} · registrert i ettertid` : kortDatoAv(r.at)}</span>
          {r.detalj ? <span className="hb-dempet hb-logg-detalj">{r.detalj}</span> : null}
          {r.lenke ? (
            <Link to={r.lenke.til} className="panel-knapp-lenke hb-logg-detalj">
              {r.lenke.tekst}
            </Link>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/** Sporingslinjen: hvor hypotesen står, med datoen som helt bare der den finnes (DD-28). */
export function sporFor(h: Hypothesis, config: HypothesisConfig | undefined, megId: string | undefined, behandler: boolean): SporLedd[] {
  const navn = (status: string) => statusNavn(config, { status: status as Hypothesis['status'] }) || status;
  const inn = h.transitions.find((t) => t.toStatus === 'vurderes');
  const mottattDato = h.submittedAt ? kortDatoAv(h.submittedAt) : undefined;
  if (h.status === 'mottatt') {
    const hvis = h.projectedDeadlineAt ? `${kortDatoAv(h.projectedDeadlineAt)} · ${behandler ? 'hvis du tar den i dag' : 'når en vurderer tar den'}` : undefined;
    return [
      { key: 'mottatt', navn: navn('mottatt'), under: mottattDato, tilstand: 'naa', ikon: hypoteseOppforing('mottatt').ikon },
      { key: 'vurderes', navn: navn('vurderes'), under: behandler ? 'når du tar den' : 'når en vurderer tar den', tilstand: 'kommende' },
      { key: 'avgjoerelse', navn: 'Avgjørelse innen', under: hvis, tilstand: 'kommende' },
    ];
  }
  if (h.status === 'vurderes') {
    const brutt = h.deadlineState === 'avgjoer_naa' || h.deadlineState === 'forsinket';
    const dager = h.deadlineAt ? -dagerTil(h.deadlineAt) : 0;
    return [
      { key: 'mottatt', navn: navn('mottatt'), under: mottattDato, tilstand: 'ferdig' },
      { key: 'vurderes', navn: navn('vurderes'), under: `${hvem(h.reviewer, megId, 'du')}${inn ? `, fra ${kortDatoAv(inn.at)}` : ''}`, tilstand: 'naa', ikon: hypoteseOppforing('vurderes').ikon },
      {
        key: 'avgjoerelse',
        navn: 'Avgjørelse innen',
        tall: h.deadlineAt ? kortDatoAv(h.deadlineAt) : undefined,
        tilstand: brutt ? 'brutt' : 'kommende',
        merknad: h.deadlineState === 'avgjoer_naa' ? 'i dag' : h.deadlineState === 'forsinket' ? `${dager} ${dager === 1 ? 'dag' : 'dager'} over` : undefined,
      },
    ];
  }
  if (h.status === 'parkert') {
    const parkert = [...h.transitions].reverse().find((t) => t.toStatus === 'parkert');
    const brutt = h.deadlineState === 'avgjoer_naa' || h.deadlineState === 'forsinket';
    return [
      { key: 'mottatt', navn: navn('mottatt'), under: mottattDato, tilstand: 'ferdig' },
      { key: 'vurderes', navn: navn('vurderes'), under: inn ? kortDatoAv(inn.at) : undefined, tilstand: 'ferdig' },
      { key: 'parkert', navn: navn('parkert'), under: parkert ? kortDatoAv(parkert.at) : undefined, tilstand: 'naa', ikon: hypoteseOppforing('parkert').ikon },
      { key: 'igjen', navn: 'Tas opp igjen', tall: brutt ? 'i dag' : h.revisitAt ? kortDato(h.revisitAt) : undefined, tilstand: brutt ? 'brutt' : 'kommende' },
    ];
  }
  const tidlige: SporLedd[] = [
    { key: 'mottatt', navn: navn('mottatt'), under: mottattDato, tilstand: 'ferdig' },
    ...(inn ? [{ key: 'vurderes', navn: navn('vurderes'), under: kortDatoAv(inn.at), tilstand: 'ferdig' as const }] : []),
  ];
  const valgtT = h.transitions.find((t) => t.toStatus === 'valgt' && t.fromStatus === 'vurderes');
  const periode = h.testStartsOn && h.testEndsOn ? `${kortDato(h.testStartsOn)}–${kortDato(h.testEndsOn)}` : undefined;
  const eierStarter = h.owner?.id === megId ? 'når du starter' : `når ${fornavn(h.owner)} starter`;
  if (h.status === 'valgt') {
    return [
      ...tidlige,
      { key: 'valgt', navn: navn('valgt'), under: valgtT ? kortDatoAv(valgtT.at) : undefined, tilstand: 'naa', ikon: hypoteseOppforing('valgt').ikon },
      { key: 'testes', navn: navn('testes'), under: eierStarter, tilstand: 'kommende' },
      { key: 'konkludert', navn: 'Konkludert', under: 'datoen settes ved start', tilstand: 'kommende' },
    ];
  }
  if (h.status === 'testes') {
    const brutt = h.deadlineState === 'avgjoer_naa' || h.deadlineState === 'forsinket';
    return [
      ...tidlige,
      { key: 'valgt', navn: navn('valgt'), under: valgtT ? kortDatoAv(valgtT.at) : undefined, tilstand: 'ferdig' },
      { key: 'testes', navn: navn('testes'), under: periode, tilstand: 'naa', ikon: hypoteseOppforing('testes').ikon },
      { key: 'konklusjon', navn: 'Konklusjon innen', tall: h.conclusionDueAt ? kortDatoAv(h.conclusionDueAt) : undefined, tilstand: brutt ? 'brutt' : 'kommende', merknad: h.deadlineState === 'avgjoer_naa' ? 'i dag' : undefined },
    ];
  }
  if (h.status === 'pa_pause') {
    const pauset = [...h.transitions].reverse().find((t) => t.toStatus === 'pa_pause');
    const brutt = h.deadlineState === 'avgjoer_naa' || h.deadlineState === 'forsinket';
    return [
      ...tidlige,
      { key: 'valgt', navn: navn('valgt'), under: valgtT ? kortDatoAv(valgtT.at) : undefined, tilstand: 'ferdig' },
      ...(pauset?.fromStatus === 'testes' ? [{ key: 'testes', navn: navn('testes'), under: testetPeriode(h), tilstand: 'ferdig' as const }] : []),
      { key: 'pa_pause', navn: navn('pa_pause'), under: pauset ? `fra ${kortDatoAv(pauset.at)}` : undefined, tilstand: 'naa', ikon: hypoteseOppforing('pa_pause').ikon },
      { key: 'igjen', navn: 'Avgjøres', tall: brutt ? 'i dag' : h.revisitAt ? kortDato(h.revisitAt) : undefined, tilstand: brutt ? 'brutt' : 'kommende' },
    ];
  }
  if (h.status === 'konkludert' || (h.status === 'stoppet' && h.startedAt)) {
    const noekkel = h.status === 'konkludert' && h.outcome ? h.outcome : h.status;
    return [
      ...tidlige,
      { key: 'valgt', navn: navn('valgt'), under: valgtT ? kortDatoAv(valgtT.at) : undefined, tilstand: 'ferdig' },
      { key: 'testes', navn: 'Testet', under: testetPeriode(h), tilstand: 'ferdig' },
      { key: 'naa', navn: statusNavn(config, h) || h.status, under: kortDatoAv(h.statusChangedAt), tilstand: 'naa', ikon: hypoteseOppforing(noekkel).ikon },
    ];
  }
  return [...tidlige, { key: 'naa', navn: statusNavn(config, h) || h.status, under: kortDatoAv(h.statusChangedAt), tilstand: 'naa', ikon: hypoteseOppforing(h.status).ikon }];
}

/**
 * Eierkandidatene i «Velg» — og i «Gi videre» når den kommer: rommets aktive personer fra config
 * (`members`, 2026-09-27), alle med minst bidragsyter. Aldri e-post, aldri deaktiverte. Tjeneren
 * avgjør uansett (422 `owner_is_decider`, 422 `validation_failed`).
 */
function eierkandidater(config: HypothesisConfig | undefined): AdminUserRef[] {
  const unike = new Map<string, AdminUserRef>();
  for (const m of config?.members ?? []) if (!unike.has(m.id)) unike.set(m.id, { id: m.id, ...(m.displayName ? { displayName: m.displayName } : {}) });
  return [...unike.values()].sort((a, b) => navnAv(a).localeCompare(navnAv(b), 'nb'));
}

/** Den innloggede i en nedtrekksliste: «Rune Aas (deg)», og uten visningsnavn bare «Deg» — aldri «en kollega (deg)». */
const megSelv = (k: AdminUserRef): string => (k.displayName?.trim() ? `${navnAv(k)} (deg)` : 'Deg');

function oppramsingNavn(navn: Array<string | null>): string {
  const unike = [...new Set(navn.filter((n): n is string => Boolean(n)))];
  if (unike.length <= 1) return unike[0] ?? '';
  return `${unike.slice(0, -1).join(', ')} og ${unike[unike.length - 1]}`;
}

/** Feilen fra tjeneren, eller `null` — for siden som eier angre-vinduet. */
export const somApiFeil = (e: unknown): ApiFeil | null => (erApiFeil(e) ? e : null);
